<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel workspace-panel--hero">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Etapas</span>
          <h2 class="workspace-title">Gestión comercial de etapas</h2>
          <p class="workspace-copy">Define precios, stock, checkout, acceso por códigos y campos extra. Las cortesías viven aparte.</p>
        </div>

        <div class="workspace-actions workspace-actions--compact">
          <button class="workspace-button" type="button" @click="toggleCatalogWorkspace">
            <i class="fas fa-sliders"></i>
            <span>Configurar</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="button" @click="openCreatePanel()">
            <i class="fas fa-plus"></i>
            <span>Nueva etapa</span>
          </button>
        </div>
      </div>

      <div class="workspace-metrics">
        <article class="workspace-metric">
          <small>Total etapas</small>
          <strong>{{ stageTickets.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Activas</small>
          <strong>{{ activeStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Ocultas</small>
          <strong>{{ hiddenStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Campos reutilizables</small>
          <strong>{{ catalogFields.length }}</strong>
        </article>
      </div>
    </article>

    <article v-if="dynamicVenueEnabled" class="glass-surface workspace-panel stage-venue-control stage-venue-control--active">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Venue activo</span>
          <h2 class="workspace-title">{{ activeDynamicVenueName }}</h2>
          <p class="workspace-copy">Este mapa está conectado al checkout. Edita su diseño o cámbialo por otro venue compatible.</p>
        </div>
        <span class="workspace-status-pill is-active">Mapa activo</span>
      </div>

      <div class="stage-venue-control__active-row">
        <div class="stage-venue-control__facts">
          <span><i class="fas fa-code-branch"></i>{{ activeDynamicVenueVersionLabel }}</span>
          <span><i class="fas fa-vector-square"></i>{{ dynamicVenueSections.length }} zonas</span>
          <span v-if="dynamicVenueProtection.has_obligations" class="is-protected"><i class="fas fa-lock"></i>Inventario protegido</span>
        </div>
        <div class="stage-venue-control__actions">
          <router-link v-if="activeVenueStudioRoute" class="workspace-button workspace-button--small" :to="activeVenueStudioRoute">
            <i class="fas fa-drafting-compass"></i>
            <span>Editar mapa</span>
          </router-link>
          <button class="workspace-button workspace-button--small workspace-button--primary" type="button" :disabled="loadingVenueLayouts || dynamicVenueSaving" @click="openVenueChangePanel">
            <i class="fas fa-exchange-alt"></i>
            <span>Cambiar mapa</span>
          </button>
        </div>
      </div>

      <p v-if="dynamicVenueControlError" class="stage-venue-control__error">{{ dynamicVenueControlError }}</p>
    </article>

    <article v-if="catalogWorkspaceOpen" class="glass-surface workspace-panel workspace-editor-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Configurar</span>
          <h2 class="workspace-title">Campos extra reutilizables</h2>
          <p class="workspace-copy">Administra el catalogo del evento y arma una configuracion lista para aplicar a todas las etapas actuales. Las nuevas etapas futuras no la heredan automaticamente.</p>
        </div>

        <button class="workspace-chip-button" type="button" @click="catalogWorkspaceOpen = false">
          <i class="fas fa-times"></i>
          <span>Cerrar</span>
        </button>
      </div>

      <div class="workspace-two-column">
        <section class="workspace-subpanel">
          <div class="workspace-subpanel__head">
            <div>
              <h3>Catalogo del evento</h3>
              <p>Estos campos se pueden reutilizar en cualquier etapa actual.</p>
            </div>
            <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('global')">
              <i class="fas fa-plus"></i>
              <span>Crear campo</span>
            </button>
          </div>

          <div v-if="catalogEditorOpen && catalogEditorContext === 'global'" class="workspace-card workspace-card--soft">
            <div class="workspace-subpanel__head workspace-subpanel__head--tight">
              <div>
                <strong>{{ editingCatalogFieldId ? 'Editar campo reutilizable' : 'Nuevo campo reutilizable' }}</strong>
                <p>Sirve para el checkout y para varias etapas.</p>
              </div>
              <button class="workspace-chip-button" type="button" @click="closeCatalogFieldEditor">
                <i class="fas fa-times"></i>
                <span>Cerrar</span>
              </button>
            </div>

            <form class="workspace-form" @submit.prevent="submitCatalogField">
              <label class="workspace-field">
                <span>Etiqueta</span>
                <input v-model="catalogForm.label" class="workspace-input" type="text" placeholder="Documento" required />
              </label>

              <label class="workspace-field">
                <span>Tipo</span>
                <select v-model="catalogForm.field_type" class="workspace-input workspace-select">
                  <option value="field">Campo libre</option>
                  <option value="multiple">Seleccion unica</option>
                </select>
              </label>

              <label class="workspace-field workspace-field--wide">
                <span>Ayuda</span>
                <input v-model="catalogForm.help_text" class="workspace-input" type="text" placeholder="Se vera debajo del campo en checkout" />
              </label>

              <label v-if="catalogForm.field_type === 'multiple'" class="workspace-field workspace-field--wide">
                <span>Opciones</span>
                <textarea v-model="catalogOptionsDraft" class="workspace-input workspace-input--textarea" rows="3" placeholder="S&#10;M&#10;L"></textarea>
                <small class="workspace-field__note">Una opcion por linea.</small>
              </label>

              <label class="workspace-inline-check workspace-inline-check--single">
                <input v-model="catalogForm.is_active" type="checkbox" />
                <span>Campo activo</span>
              </label>

              <div class="workspace-actions">
                <button class="workspace-button workspace-button--primary" type="submit" :disabled="catalogSaving">
                  <i class="fas fa-save"></i>
                  <span>{{ catalogSaving ? 'Guardando...' : editingCatalogFieldId ? 'Guardar campo' : 'Crear campo' }}</span>
                </button>
              </div>
            </form>
          </div>

          <div v-if="!catalogFields.length" class="workspace-empty">Todavia no hay campos reutilizables.</div>
          <div v-else class="workspace-list-stack">
            <article v-for="field in catalogFields" :key="field.id" class="workspace-card workspace-card--soft">
              <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                <div>
                  <strong>{{ field.label }}</strong>
                  <p>{{ fieldTypeLabel(field.field_type) }} · {{ field.slug }}</p>
                </div>
                <span class="workspace-badge">{{ field.assigned_tickets_count || 0 }} etapas</span>
              </div>

              <p v-if="field.help_text" class="workspace-table-subcopy">{{ field.help_text }}</p>
              <div v-if="Array.isArray(field.options) && field.options.length" class="workspace-chip-row">
                <span v-for="option in field.options" :key="`${field.id}-${option}`" class="workspace-badge">{{ option }}</span>
              </div>

              <div class="workspace-inline-actions">
                <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('global', field)">
                  <i class="fas fa-pen"></i>
                  <span>Editar</span>
                </button>
                <button class="workspace-chip-button" type="button" @click="toggleGlobalAssignment(field.id)">
                  <i :class="isFieldSelected(globalAssignments, field.id) ? 'fas fa-minus' : 'fas fa-plus'"></i>
                  <span>{{ isFieldSelected(globalAssignments, field.id) ? 'Quitar de config' : 'Sumar a config' }}</span>
                </button>
                <button class="workspace-chip-button" type="button" @click="deleteCatalogField(field)">
                  <i class="fas fa-trash-alt"></i>
                  <span>Eliminar</span>
                </button>
              </div>
            </article>
          </div>
        </section>

        <section class="workspace-subpanel">
          <div class="workspace-subpanel__head">
            <div>
              <h3>Configuracion global para etapas actuales</h3>
              <p>Arma una combinacion reusable y luego aplicala explicitamente a todas las etapas actuales.</p>
            </div>
          </div>

          <div class="workspace-card workspace-card--soft">
            <div v-if="!globalAssignmentDetails.length" class="workspace-empty workspace-empty--compact">Selecciona campos del catalogo para preparar una configuracion global.</div>
            <div v-else class="workspace-list-stack">
              <article v-for="(assignment, index) in globalAssignmentDetails" :key="`global-${assignment.field_id}`" class="workspace-assignment-card">
                <div>
                  <strong>{{ assignment.label }}</strong>
                  <p>{{ fieldTypeLabel(assignment.field_type) }}</p>
                </div>
                <div class="workspace-assignment-controls">
                  <label class="workspace-inline-check workspace-inline-check--single">
                    <input :checked="assignment.is_required" type="checkbox" @change="toggleAssignmentRequired(globalAssignments, assignment.field_id)" />
                    <span>Obligatorio</span>
                  </label>
                  <div class="workspace-inline-actions">
                    <button class="workspace-chip-button" type="button" :disabled="index === 0" @click="moveAssignment(globalAssignments, index, -1)">
                      <i class="fas fa-arrow-up"></i>
                    </button>
                    <button class="workspace-chip-button" type="button" :disabled="index === globalAssignmentDetails.length - 1" @click="moveAssignment(globalAssignments, index, 1)">
                      <i class="fas fa-arrow-down"></i>
                    </button>
                    <button class="workspace-chip-button" type="button" @click="toggleGlobalAssignment(assignment.field_id)">
                      <i class="fas fa-minus"></i>
                      <span>Quitar</span>
                    </button>
                  </div>
                </div>
              </article>
            </div>

            <div class="workspace-actions workspace-actions--split-top">
               <button class="workspace-button workspace-button--primary" type="button" :disabled="applyingGlobalAssignments || !stageTickets.length" @click="applyGlobalAssignmentsToCurrentStages">
                <i class="fas fa-layer-group"></i>
                <span>{{ applyingGlobalAssignments ? 'Aplicando...' : 'Aplicar a todas las etapas actuales' }}</span>
              </button>
            </div>
            <p class="workspace-field__note">Esta acción actualiza solo las etapas que ya existen en este evento ahora mismo.</p>
          </div>

          <div class="workspace-card workspace-card--soft">
            <div class="workspace-subpanel__head workspace-subpanel__head--tight">
              <div>
                <strong>Preview checkout</strong>
                <p>Asi se verian estos campos dentro de una etapa.</p>
              </div>
            </div>
            <div class="workspace-preview-stack">
              <template v-if="globalAssignmentDetails.length">
                <div v-for="field in globalAssignmentDetails" :key="`global-preview-${field.field_id}`" class="workspace-preview-field">
                  <label>
                    {{ field.label }}
                    <span v-if="field.is_required">*</span>
                  </label>
                  <input v-if="field.field_type === 'field'" class="workspace-input" type="text" :placeholder="field.help_text || 'Respuesta del invitado'" disabled />
                  <select v-else class="workspace-input workspace-select" disabled>
                    <option value="">Selecciona una opcion</option>
                    <option v-for="option in field.options" :key="`${field.field_id}-global-preview-${option}`" :value="option">{{ option }}</option>
                  </select>
                  <small v-if="field.help_text" class="workspace-field__note">{{ field.help_text }}</small>
                </div>
              </template>
              <div v-else class="workspace-empty workspace-empty--compact">Agrega al menos un campo para ver el preview.</div>
            </div>
          </div>
        </section>
      </div>
    </article>

    <WorkspaceWizardModal
      :model-value="editorOpen"
      :kicker="stageEditorModalKicker"
      :title="stageEditorModalTitle"
      :copy="stageEditorModalCopy"
      size="xl"
      @close-request="resetForm"
    >
      <section v-if="showFirstStageModeChooser" class="stage-entry-picker">
        <div class="stage-entry-picker__intro">
          <span class="stage-entry-picker__step">Primera etapa</span>
          <p>Elige cómo quieres organizar la venta. Después podrás completar precio, stock, acceso y todos los detalles.</p>
        </div>

        <div class="stage-entry-grid">
          <button class="stage-entry-card stage-entry-card--list" type="button" @click="selectFirstStageMode('list')">
            <span class="stage-entry-card__glow" aria-hidden="true"></span>
            <span class="stage-entry-card__topline">
              <span class="stage-entry-card__icon"><i class="fas fa-list-ul"></i></span>
              <span class="stage-entry-card__status"><i></i> Siempre disponible</span>
            </span>

            <span class="stage-entry-card__copy">
              <small>Venta flexible</small>
              <strong>Por etapas / lista</strong>
              <span>Crea una lista clara de etapas con su propio precio, cupo y reglas de acceso.</span>
            </span>

            <span class="stage-entry-list-preview" aria-hidden="true">
              <span class="stage-entry-list-preview__toolbar">
                <i></i><i></i><i></i>
                <em>ETAPAS</em>
              </span>
              <span
                v-for="index in 4"
                :key="`first-stage-list-preview-${index}`"
                class="stage-entry-list-preview__row"
                :style="{ '--row-index': index }"
              >
                <span class="stage-entry-list-preview__number">0{{ index }}</span>
                <span class="stage-entry-list-preview__lines"><i></i><i></i></span>
                <span class="stage-entry-list-preview__price"></span>
                <span class="stage-entry-list-preview__check"><i class="fas fa-check"></i></span>
              </span>
            </span>

            <span class="stage-entry-card__action">
              <span>Crear con lista</span>
              <i class="fas fa-arrow-right"></i>
            </span>
          </button>

          <button
            class="stage-entry-card stage-entry-card--venue"
            type="button"
            :disabled="firstStageVenueLoading"
            @click="selectFirstStageMode('venue')"
          >
            <span class="stage-entry-card__glow" aria-hidden="true"></span>
            <span class="stage-entry-card__topline">
              <span class="stage-entry-card__icon"><i class="fas fa-map-marked-alt"></i></span>
              <span :class="['stage-entry-card__status', { 'is-ready': dynamicVenueEditorAvailable }]">
                <i></i>{{ firstStageVenueStatusLabel }}
              </span>
            </span>

            <span class="stage-entry-card__copy">
              <small>Experiencia visual</small>
              <strong>Por Venue / mapa</strong>
              <span>{{ firstStageVenueDescription }}</span>
            </span>

            <span class="stage-entry-map-preview" aria-hidden="true">
              <svg viewBox="0 0 420 230">
                <defs>
                  <linearGradient id="firstStageVenueFloor" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stop-color="#151c2b" />
                    <stop offset="100%" stop-color="#070a11" />
                  </linearGradient>
                  <linearGradient id="firstStageVenueRoute" x1="0" x2="1">
                    <stop offset="0%" stop-color="#5f347d" />
                    <stop offset="100%" stop-color="#b58ad8" />
                  </linearGradient>
                </defs>
                <rect class="stage-entry-map-preview__floor" x="8" y="8" width="404" height="214" rx="30" />
                <path class="stage-entry-map-preview__route" d="M 54 178 C 100 146, 116 180, 160 142 S 238 76, 286 108 S 340 86, 374 52" />
                <g class="stage-entry-map-preview__zones">
                  <path d="M42 44 H168 L148 112 H54 Z" />
                  <path d="M180 35 H292 L310 96 L196 112 Z" />
                  <path d="M64 128 L154 116 L188 190 H48 Z" />
                  <path d="M210 126 L342 108 L376 184 L224 196 Z" />
                </g>
                <g class="stage-entry-map-preview__seats">
                  <circle cx="80" cy="67" r="5" /><circle cx="104" cy="67" r="5" /><circle cx="128" cy="67" r="5" />
                  <circle cx="74" cy="88" r="5" /><circle cx="98" cy="88" r="5" /><circle cx="122" cy="88" r="5" />
                  <circle cx="222" cy="59" r="5" /><circle cx="246" cy="59" r="5" /><circle cx="270" cy="59" r="5" />
                  <circle cx="228" cy="82" r="5" /><circle cx="252" cy="82" r="5" /><circle cx="276" cy="82" r="5" />
                </g>
                <g class="stage-entry-map-preview__pin" transform="translate(286 108)">
                  <circle r="18" />
                  <circle r="6" />
                </g>
              </svg>
              <span class="stage-entry-map-preview__legend"><i></i> Selección visual en vivo</span>
            </span>

            <span class="stage-entry-card__action">
              <span>{{ firstStageVenueActionLabel }}</span>
              <i :class="firstStageVenueLoading ? 'fas fa-spinner fa-spin' : 'fas fa-arrow-right'"></i>
            </span>
          </button>
        </div>

        <p class="stage-entry-picker__note"><i class="fas fa-magic"></i> Esta elección solo aparece al crear la primera etapa del evento.</p>
      </section>

      <section v-else-if="showFirstStageVenueSetup" class="stage-venue-onboarding">
        <button class="stage-venue-onboarding__back" type="button" @click="returnToFirstStageModes">
          <i class="fas fa-arrow-left"></i>
          <span>{{ venueSelectorBackLabel }}</span>
        </button>

        <div v-if="publishedVenueLayouts.length" class="stage-venue-onboarding__shell">
          <aside class="stage-venue-onboarding__selector">
            <div class="stage-venue-onboarding__heading">
              <span class="workspace-kicker">Tus venues</span>
              <h3>Selecciona el mapa</h3>
              <p>El mapa elegido quedará conectado a este evento.</p>
            </div>

            <div class="stage-venue-onboarding__options" role="radiogroup" aria-label="Venues publicados">
              <button
                v-for="layout in publishedVenueLayouts"
                :key="`first-stage-venue-${layout.id}`"
                type="button"
                :class="['stage-venue-option', { 'is-active': String(dynamicVenueSelectedLayoutId) === String(layout.id) }]"
                role="radio"
                :aria-checked="String(dynamicVenueSelectedLayoutId) === String(layout.id)"
                @click="selectFirstStageVenueLayout(layout)"
              >
                <span class="stage-venue-option__icon"><i class="fas fa-map"></i></span>
                <span class="stage-venue-option__copy">
                  <strong>{{ layout.name }}</strong>
                  <small>{{ layout.source_venue_name || 'Venue Studio' }}</small>
                  <em>v{{ layout.published_version.version }} publicada</em>
                </span>
                <span class="stage-venue-option__check"><i class="fas fa-check"></i></span>
              </button>
            </div>
          </aside>

          <article class="stage-venue-onboarding__preview">
            <div class="stage-venue-onboarding__preview-head">
              <div>
                <span class="workspace-kicker">Preview gráfico</span>
                <h3>{{ selectedDynamicVenueLayout ? selectedDynamicVenueLayout.name : 'Elige un venue' }}</h3>
              </div>
              <span v-if="dynamicVenueSelectedIsAssigned" class="stage-venue-onboarding__active-badge"><i></i> Activo ahora</span>
            </div>

            <div v-if="firstStageVenuePreviewLoading" class="stage-venue-preview-loading" aria-live="polite">
              <span class="stage-venue-preview-loading__map"></span>
              <span><i></i><i></i></span>
              <p>Cargando mapa publicado...</p>
            </div>

            <div v-else-if="firstStageVenuePreviewError" class="stage-venue-preview-error">
              <i class="fas fa-exclamation-triangle"></i>
              <strong>No pudimos cargar el preview</strong>
              <span>{{ firstStageVenuePreviewError }}</span>
              <button type="button" @click="reloadFirstStageVenuePreview">Reintentar</button>
            </div>

            <div v-else-if="firstStageVenuePreviewSections.length" class="stage-venue-preview-map">
              <HostDynamicVenueMiniMap
                :sections="firstStageVenuePreviewSections"
                :view-box="firstStageVenuePreviewViewBox"
                :seats="firstStageVenuePreviewSeats"
                readonly-sections
              />
              <div class="stage-venue-preview-map__facts">
                <span><strong>{{ firstStageVenuePreviewSections.length }}</strong> zonas</span>
                <span><strong>{{ firstStageVenuePreviewSeats.length }}</strong> asientos / mesas</span>
              </div>
            </div>

            <div v-else class="stage-venue-preview-error stage-venue-preview-error--empty">
              <i class="fas fa-map"></i>
              <strong>Este venue no tiene zonas publicadas</strong>
              <span>Ábrelo en Venue Studio, agrega zonas y publica una nueva versión.</span>
            </div>

            <p v-if="dynamicVenueControlError" class="stage-venue-onboarding__error">{{ dynamicVenueControlError }}</p>

            <div class="stage-venue-onboarding__actions">
              <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="returnToFirstStageModes">{{ venueSelectionPurpose === 'manage' ? 'Cerrar' : 'Cancelar' }}</button>
              <button
                class="workspace-button workspace-button--small workspace-button--primary"
                type="button"
                :disabled="!canContinueWithFirstStageVenue"
                @click="confirmFirstStageVenue"
              >
                <i :class="dynamicVenueSaving ? 'fas fa-spinner fa-spin' : 'fas fa-map-marked-alt'"></i>
                <span>{{ dynamicVenueSaving ? 'Cambiando...' : venueSelectorConfirmLabel }}</span>
              </button>
            </div>
          </article>
        </div>

        <div v-else class="stage-venue-onboarding-empty">
          <div class="stage-venue-onboarding-empty__visual" aria-hidden="true">
            <span class="stage-venue-onboarding-empty__grid"></span>
            <span class="stage-venue-onboarding-empty__zone stage-venue-onboarding-empty__zone--one"></span>
            <span class="stage-venue-onboarding-empty__zone stage-venue-onboarding-empty__zone--two"></span>
            <span class="stage-venue-onboarding-empty__zone stage-venue-onboarding-empty__zone--three"></span>
            <span class="stage-venue-onboarding-empty__cursor"><i class="fas fa-location-arrow"></i></span>
          </div>
          <div class="stage-venue-onboarding-empty__copy">
            <span class="workspace-kicker">Venue Studio</span>
            <h3>No tienes ningún venue creado todavía</h3>
            <p>Diseña tu mapa con zonas, mesas o asientos. Cuando lo publiques podrás volver y conectarlo a esta primera etapa.</p>
            <div class="stage-venue-onboarding__actions">
              <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="returnToFirstStageModes">{{ venueSelectionPurpose === 'manage' ? 'Cerrar' : 'Volver' }}</button>
              <button class="workspace-button workspace-button--small workspace-button--primary" type="button" @click="openVenueStudioFromFirstStage">
                <i class="fas fa-drafting-compass"></i>
                <span>Ir a Venue Studio</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <form v-else id="stage-editor-form" class="stage-modal" @submit.prevent="submitStage">
        <section class="stage-modal__section">
          <div class="stage-modal__section-head">
            <div>
              <span class="workspace-kicker">Base</span>
              <h3>Datos base</h3>
            </div>
          </div>

          <div class="stage-base-facts">
            <span class="workspace-badge workspace-badge--mint">{{ stageTaxPreview.grossAmount > 0 ? `$${formatMoney(stageTaxPreview.grossAmount)}` : '$0.00' }}</span>
            <span class="workspace-badge">{{ Number(form.initial_quantity) || 0 }} boletos</span>
            <span class="workspace-badge">{{ Number(form.max_quantity) || 0 }} por compra</span>
            <span v-if="form.tax_enabled && stageTaxPreview.taxAmount > 0" class="workspace-badge">{{ form.tax_label || 'IVA' }} ${{ formatMoney(stageTaxPreview.taxAmount) }}</span>
          </div>

          <div class="stage-modal__grid stage-modal__grid--base">
            <label class="workspace-field stage-modal__field--hero">
              <span>Nombre</span>
              <input v-model="form.name" class="workspace-input" type="text" placeholder="General Early Bird" required />
            </label>

            <label class="workspace-field">
              <span>Precio</span>
              <input v-model.number="form.price" class="workspace-input" type="number" min="0" step="0.01" required />
              <small class="workspace-field__note">{{ form.tax_enabled && !form.tax_price_includes_tax ? 'Precio base antes de impuesto.' : 'Precio que verá el comprador.' }}</small>
            </label>

            <label class="workspace-field">
              <span>Stock</span>
              <input v-model.number="form.initial_quantity" class="workspace-input" type="number" min="1" required />
            </label>

            <label class="workspace-field">
              <span>Máximo por compra</span>
              <input v-model.number="form.max_quantity" class="workspace-input" type="number" min="1" required />
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Descripción</span>
              <textarea v-model="form.description" class="workspace-input workspace-input--textarea" rows="4" placeholder="Qué incluye, para quién aplica y por qué conviene comprar esta etapa."></textarea>
            </label>

            <div class="stage-tax-card workspace-field--wide">
              <div class="stage-tax-card__head">
                <label class="workspace-inline-check workspace-inline-check--single">
                  <input v-model="form.tax_enabled" type="checkbox" />
                  <span>Agregar IVA / VAT / Sales tax</span>
                </label>
                <span v-if="form.tax_enabled" class="workspace-badge workspace-badge--mint">Final ${{ formatMoney(stageTaxPreview.grossAmount) }}</span>
              </div>
              <p>Opcional por etapa. Si está activo, el fee FOMO se calcula sobre el precio final con impuesto.</p>

              <div v-if="form.tax_enabled" class="stage-tax-card__grid">
                <label class="workspace-field">
                  <span>Nombre del impuesto</span>
                  <input v-model="form.tax_label" class="workspace-input" type="text" maxlength="40" placeholder="IVA" />
                </label>

                <label class="workspace-field">
                  <span>% impuesto</span>
                  <input v-model.number="form.tax_percent" class="workspace-input" type="number" min="0" step="0.01" placeholder="15" />
                </label>

                <label class="workspace-inline-check workspace-inline-check--single stage-tax-card__included">
                  <input v-model="form.tax_price_includes_tax" type="checkbox" />
                  <span>El precio ya incluye este impuesto</span>
                </label>
              </div>

              <div v-if="form.tax_enabled" class="stage-tax-preview">
                <span>Base ${{ formatMoney(stageTaxPreview.baseAmount) }}</span>
                <span>{{ form.tax_label || 'IVA' }} ${{ formatMoney(stageTaxPreview.taxAmount) }}</span>
                <strong>Total ${{ formatMoney(stageTaxPreview.grossAmount) }}</strong>
              </div>
            </div>
          </div>
        </section>

        <section class="stage-modal__section">
          <div class="stage-modal__section-head">
            <div>
              <span class="workspace-kicker">Checkout y códigos</span>
              <h3>Visibilidad y acceso</h3>
            </div>
          </div>

          <div class="stage-inline-group">
            <span class="stage-inline-group__label">¿Visible en checkout?</span>
            <div class="stage-toggle-pills" role="tablist" aria-label="Visibilidad en checkout">
              <button type="button" :class="['stage-toggle-pill', { 'is-active': stageCheckoutVisibility === 'checkout' }]" @click="stageCheckoutVisibility = 'checkout'">Sí</button>
              <button type="button" :class="['stage-toggle-pill', { 'is-active': stageCheckoutVisibility === 'hidden' }]" @click="stageCheckoutVisibility = 'hidden'">No</button>
            </div>
            <p class="stage-inline-helper">Si eliges <strong>Sí</strong>, la etapa aparece en la compra general del evento. Si eliges <strong>No</strong>, queda fuera del checkout y solo se abre por link privado o por códigos, según lo que definas abajo.</p>
          </div>

          <div class="stage-scope-shell">
            <div v-if="showStageCodeApplicability" class="stage-inline-group">
              <span class="stage-inline-group__label">¿A qué quieres aplicar la etapa?</span>
              <div class="stage-scope-picker" role="tablist" aria-label="Disponibilidad con códigos">
                <button type="button" :class="['stage-scope-pill', { 'is-active': stageCodeAvailability === 'all' }]" @click="stageCodeAvailability = 'all'">A todos los códigos</button>
                <button type="button" :class="['stage-scope-pill', { 'is-active': stageCodeAvailability === 'selected' }]" @click="stageCodeAvailability = 'selected'">Solo a ciertos códigos</button>
                <button type="button" :class="['stage-scope-pill', { 'is-active': stageCodeAvailability === 'none' }]" @click="stageCodeAvailability = 'none'">A ningún código</button>
              </div>
            </div>
            <p v-if="!showStageCodeApplicability && stageCheckoutVisibility === 'hidden'" class="stage-availability-note">Al guardar se generará un link privado interno para esta etapa.</p>
            <p v-else-if="showStageCodeApplicability && stageCheckoutVisibility === 'hidden' && stageCodeAvailability === 'none'" class="stage-availability-note">Se generará un link privado interno al guardar. Ese acceso no se mezcla con tus códigos del evento.</p>
            <p v-else-if="showStageCodeApplicability" class="stage-availability-note">{{ stageVisibilityNarrative }}</p>
          </div>

          <div v-if="stageCodeAvailability === 'selected'" class="stage-code-shell">
            <div class="stage-code-shell__head">
              <div>
                <strong>Códigos elegidos</strong>
                <p>Selecciona solo los códigos que deben abrir esta etapa.</p>
              </div>
              <span class="workspace-badge">{{ selectedCodeDetails.length }} seleccionados</span>
            </div>

            <label class="workspace-field workspace-field--wide">
              <span>Buscar código</span>
              <input v-model.trim="stageCodeSearch" class="workspace-input" type="text" placeholder="VIP, influencer, acceso staff..." />
            </label>

            <div v-if="selectedCodeDetails.length" class="stage-code-chip-row">
              <button v-for="eventCode in selectedCodeDetails" :key="`selected-code-${eventCode.id}`" class="stage-code-chip" type="button" @click="toggleStageCodeSelection(eventCode.id)">
                <span>{{ eventCode.code }}</span>
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div v-if="!filteredActiveCodes.length" class="workspace-empty workspace-empty--compact">No encontramos códigos activos con esa búsqueda.</div>
            <div v-else class="stage-code-option-grid">
              <button
                v-for="eventCode in filteredActiveCodes"
                :key="`code-option-${eventCode.id}`"
                type="button"
                :class="['stage-code-option', { 'is-active': isSelectedStageCode(eventCode.id) }]"
                @click="toggleStageCodeSelection(eventCode.id)"
              >
                <strong>{{ eventCode.code }}</strong>
                <small>{{ eventCode.label || 'Sin etiqueta' }}</small>
              </button>
            </div>
          </div>

          <div class="stage-activation-row">
            <label class="workspace-inline-check workspace-inline-check--single">
              <input v-model="form.status" type="checkbox" />
              <span>Etapa activa</span>
            </label>
          </div>

        </section>

        <section v-if="dynamicVenueEditorAvailable" class="stage-modal__section">
          <div class="stage-modal__section-head">
            <div>
              <span class="workspace-kicker">Mapa dinámico</span>
              <h3>Venue conectado</h3>
            </div>
            <p>{{ dynamicVenueSelectionSummary }}</p>
          </div>

          <div class="stage-inline-group">
            <span class="stage-inline-group__label">Tipo de venta</span>
            <div class="stage-scope-picker" role="tablist" aria-label="Modo de mapa dinámico">
              <button type="button" :class="['stage-scope-pill', { 'is-active': form.dynamic_venue_mode === 'none' }]" @click="setDynamicVenueMode('none')">
                <strong>Normal</strong>
                <small>Fuera del mapa</small>
              </button>
              <button type="button" :class="['stage-scope-pill', { 'is-active': form.dynamic_venue_mode !== 'none' }]" @click="activateDynamicVenueSelection">
                <strong>Venue Studio</strong>
                <small>Click en mapa</small>
              </button>
            </div>
            <p class="stage-inline-helper">{{ dynamicVenueModeHelper }}</p>
          </div>

          <div v-if="form.dynamic_venue_mode !== 'none'" class="stage-venue-picker">
            <div class="stage-venue-picker__head">
              <div>
                <strong>{{ dynamicVenuePickerTitle }}</strong>
                <span>{{ dynamicVenuePickerHint }}</span>
              </div>
              <button v-if="form.dynamic_venue_section_ids.length" class="workspace-chip-button" type="button" @click="clearDynamicVenueSections">
                <i class="fas fa-times"></i>
                <span>Limpiar</span>
              </button>
            </div>

            <div v-if="dynamicVenueVisibleSections.length" class="stage-venue-visual">
              <svg class="stage-venue-map" :viewBox="dynamicVenueViewBoxString" role="img" aria-label="Seleccion visual del venue" @click="handleDynamicVenueMapClick">
                <defs>
                  <linearGradient id="stageVenueFloorDepth" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stop-color="#18202d" />
                    <stop offset="48%" stop-color="#0a0f18" />
                    <stop offset="100%" stop-color="#03060c" />
                  </linearGradient>
                </defs>
                <rect class="stage-venue-map__floor" :x="dynamicVenueMapViewBox.x" :y="dynamicVenueMapViewBox.y" :width="dynamicVenueMapViewBox.width" :height="dynamicVenueMapViewBox.height" rx="34" />
                <g class="stage-venue-map__layers">
                  <template v-for="item in dynamicVenueRenderItems" :key="`venue-map-layer-${item.key}`">
                  <g
                    v-for="section in item.type === 'section' ? [item.element] : []"
                    :key="`venue-map-section-${section.id}`"
                    class="stage-venue-map__section"
                    :class="{ 'is-active': isDynamicVenueSectionSelected(section), 'is-hovered': isDynamicVenueSectionHovered(section) }"
                    :style="dynamicVenueDepthStyle(section, 'section')"
                    role="button"
                    tabindex="0"
                    @click.stop="selectDynamicVenueSectionFromMap(section)"
                    @keydown.enter.prevent="selectDynamicVenueSectionFromMap(section)"
                    @keydown.space.prevent="selectDynamicVenueSectionFromMap(section)"
                  >
                  <template v-for="area in dynamicVenueSectionVisualAreas(section)" :key="area.key">
                    <path
                      v-if="dynamicVenueSectionAreaShapeType(area) === 'path'"
                      :d="dynamicVenueSectionAreaPath(area)"
                      :fill="dynamicVenueSectionFill(section)"
                      :stroke="section.color || '#8e6bc4'"
                      :fill-rule="dynamicVenueSectionAreaFillRule(area)"
                      :clip-rule="dynamicVenueSectionAreaFillRule(area)"
                    />
                    <ellipse
                      v-else-if="dynamicVenueSectionAreaShapeType(area) === 'ellipse'"
                      :cx="dynamicVenueSectionAreaCenterX(area)"
                      :cy="dynamicVenueSectionAreaCenterY(area)"
                      :rx="area.shape.w / 2"
                      :ry="area.shape.h / 2"
                      :fill="dynamicVenueSectionFill(section)"
                      :stroke="section.color || '#8e6bc4'"
                    />
                    <polygon
                      v-else-if="dynamicVenueSectionAreaShapeType(area) === 'polygon'"
                      :points="dynamicVenueSectionAreaPolygonPoints(area)"
                      :fill="dynamicVenueSectionFill(section)"
                      :stroke="section.color || '#8e6bc4'"
                    />
                    <rect
                      v-else
                      :x="area.shape.x"
                      :y="area.shape.y"
                      :width="area.shape.w"
                      :height="area.shape.h"
                      :rx="area.shape.rx || 24"
                      :fill="dynamicVenueSectionFill(section)"
                      :stroke="section.color || '#8e6bc4'"
                    />
                  </template>
                  <text :x="dynamicVenueSectionCenterX(section)" :y="dynamicVenueSectionCenterY(section) - 6" text-anchor="middle">{{ section.name }}</text>
                  <text class="stage-venue-map__type" :x="dynamicVenueSectionCenterX(section)" :y="dynamicVenueSectionCenterY(section) + 24" text-anchor="middle">{{ dynamicVenueSectionShortTypeLabel(section) }}</text>
                  </g>
                  <g v-for="object in item.type === 'object' ? [item.element] : []" :key="`venue-map-object-${object.id}`" class="stage-venue-map__object" :style="dynamicVenueDepthStyle(object, 'object')" aria-hidden="true">
                    <g v-if="dynamicVenueObjectIsSemantic(object)" class="stage-venue-map__semantic" :class="`is-${dynamicVenueObjectSemanticType(object)}`">
                      <template v-if="dynamicVenueObjectSemanticType(object) === 'stage'">
                        <ellipse
                          v-if="dynamicVenueStageVariant(object) === 'arena'"
                          :cx="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w / 2"
                          :cy="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h / 2"
                          :rx="dynamicVenueObjectBox(object).w * 0.43"
                          :ry="dynamicVenueObjectBox(object).h * 0.36"
                          :fill="dynamicVenueObjectFill(object)"
                          :stroke="dynamicVenueObjectColor(object)"
                        />
                        <polygon
                          v-else
                          :points="dynamicVenueStageDeckPoints(object)"
                          :fill="dynamicVenueObjectFill(object)"
                          :stroke="dynamicVenueObjectColor(object)"
                        />
                        <path
                          v-if="dynamicVenueStageVariant(object) === 'runway' || dynamicVenueStageVariant(object) === 'corner'"
                          :d="dynamicVenueStageLinePath(object)"
                          fill="none"
                          :stroke="dynamicVenueObjectColor(object)"
                        />
                        <ellipse
                          v-else-if="dynamicVenueStageVariant(object) === 'arena'"
                          :cx="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w / 2"
                          :cy="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h / 2"
                          :rx="dynamicVenueObjectBox(object).w * 0.26"
                          :ry="dynamicVenueObjectBox(object).h * 0.2"
                          fill="none"
                          :stroke="dynamicVenueObjectColor(object)"
                        />
                      </template>
                      <template v-else-if="dynamicVenueObjectSemanticType(object) === 'bathroom'">
                        <rect
                          :x="dynamicVenueObjectBox(object).x"
                          :y="dynamicVenueObjectBox(object).y"
                          :width="dynamicVenueObjectBox(object).w"
                          :height="dynamicVenueObjectBox(object).h"
                          :rx="dynamicVenueObjectBox(object).rx || 14"
                          :fill="dynamicVenueObjectFill(object)"
                          :stroke="dynamicVenueObjectColor(object)"
                        />
                        <circle
                          :cx="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w * 0.34"
                          :cy="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h * 0.28"
                          :r="dynamicVenueObjectBox(object).h * 0.1"
                          :fill="dynamicVenueObjectColor(object)"
                        />
                        <path
                          v-if="dynamicVenueBathroomVariant(object) === 'accessible'"
                          :d="dynamicVenueBathroomAccessiblePath(object)"
                          fill="none"
                          :stroke="dynamicVenueObjectColor(object)"
                        />
                        <text
                          :x="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w * 0.7"
                          :y="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h * 0.64"
                          text-anchor="middle"
                          :fill="dynamicVenueObjectColor(object)"
                        >{{ dynamicVenueBathroomText(object) }}</text>
                      </template>
                      <text
                        :x="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w / 2"
                        :y="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h + 24"
                        text-anchor="middle"
                        :fill="dynamicVenueObjectColor(object)"
                      >{{ object.label }}</text>
                    </g>
                    <ellipse
                      v-else-if="dynamicVenueObjectShapeType(object) === 'ellipse'"
                      :cx="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w / 2"
                      :cy="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h / 2"
                      :rx="dynamicVenueObjectBox(object).w / 2"
                      :ry="dynamicVenueObjectBox(object).h / 2"
                      :fill="dynamicVenueObjectFill(object)"
                      :stroke="dynamicVenueObjectColor(object)"
                    />
                    <line
                      v-else-if="dynamicVenueObjectShapeType(object) === 'line' || dynamicVenueObjectShapeType(object) === 'arrow'"
                      :x1="dynamicVenueObjectBox(object).x"
                      :x2="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w"
                      :y1="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h / 2"
                      :y2="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h / 2"
                      :stroke="dynamicVenueObjectColor(object)"
                    />
                    <text
                      v-else-if="dynamicVenueObjectShapeType(object) === 'text'"
                      :x="dynamicVenueObjectBox(object).x + dynamicVenueObjectBox(object).w / 2"
                      :y="dynamicVenueObjectBox(object).y + dynamicVenueObjectBox(object).h / 2 + 8"
                      text-anchor="middle"
                      :fill="dynamicVenueObjectColor(object)"
                      :style="dynamicVenueObjectTextStyle(object)"
                    >{{ object.label }}</text>
                    <rect
                      v-else
                      :x="dynamicVenueObjectBox(object).x"
                      :y="dynamicVenueObjectBox(object).y"
                      :width="dynamicVenueObjectBox(object).w"
                      :height="dynamicVenueObjectBox(object).h"
                      :rx="dynamicVenueObjectBox(object).rx || 14"
                      :fill="dynamicVenueObjectFill(object)"
                      :stroke="dynamicVenueObjectColor(object)"
                    />
                  </g>
                  <g
                    v-for="seat in item.type === 'seat' ? [item.element] : []"
                    :key="`venue-map-seat-${seat.key}`"
                    class="stage-venue-map__unit"
                    :class="{ 'is-table': seat.is_table, 'is-active': isDynamicVenueSectionIdSelected(seat.section_id), 'is-loose': dynamicVenueSeatSectionIsHidden(seat) }"
                    :style="dynamicVenueDepthStyle(seat, 'seat')"
                    :transform="dynamicVenueSeatTransform(seat)"
                    role="button"
                    tabindex="0"
                    @click.stop="selectDynamicVenueSectionById(seat.section_id)"
                    @keydown.enter.prevent="selectDynamicVenueSectionById(seat.section_id)"
                    @keydown.space.prevent="selectDynamicVenueSectionById(seat.section_id)"
                  >
                    <rect :width="dynamicVenueSeatWidth(seat)" :height="dynamicVenueSeatHeight(seat)" :rx="seat.is_table ? 18 : 7" />
                    <text v-if="seat.is_table || dynamicVenueSeatSectionIsHidden(seat)" :x="dynamicVenueSeatWidth(seat) / 2" :y="dynamicVenueSeatHeight(seat) / 2 + 4" text-anchor="middle">{{ seat.label }}</text>
                  </g>
                  </template>
                </g>
                <g class="stage-venue-map__hit-layer" aria-hidden="true">
                  <g
                    v-for="section in dynamicVenueMapHitSections"
                    :key="`venue-map-hit-${section.id}`"
                    class="stage-venue-map__hit-zone"
                    @click.stop="selectDynamicVenueSectionFromMap(section)"
                    @mouseenter="hoverDynamicVenueSection(section)"
                    @mouseleave="clearDynamicVenueSectionHover(section)"
                  >
                    <template v-for="area in dynamicVenueSectionVisualAreas(section)" :key="`venue-map-hit-area-${section.id}-${area.key}`">
                      <path
                        v-if="dynamicVenueSectionAreaShapeType(area) === 'path'"
                        class="stage-venue-map__hit-area"
                        :d="dynamicVenueSectionAreaPath(area)"
                        :fill-rule="dynamicVenueSectionAreaFillRule(area)"
                        :clip-rule="dynamicVenueSectionAreaFillRule(area)"
                      />
                      <ellipse
                        v-else-if="dynamicVenueSectionAreaShapeType(area) === 'ellipse'"
                        class="stage-venue-map__hit-area"
                        :cx="dynamicVenueSectionAreaCenterX(area)"
                        :cy="dynamicVenueSectionAreaCenterY(area)"
                        :rx="area.shape.w / 2"
                        :ry="area.shape.h / 2"
                      />
                      <polygon
                        v-else-if="dynamicVenueSectionAreaShapeType(area) === 'polygon'"
                        class="stage-venue-map__hit-area"
                        :points="dynamicVenueSectionAreaPolygonPoints(area)"
                      />
                      <rect
                        v-else
                        class="stage-venue-map__hit-area"
                        :x="area.shape.x"
                        :y="area.shape.y"
                        :width="area.shape.w"
                        :height="area.shape.h"
                        :rx="area.shape.rx || 24"
                      />
                    </template>
                  </g>
                </g>
              </svg>

              <div class="stage-venue-grid stage-venue-grid--compact">
                <button
                  v-for="section in dynamicVenueVisibleSections"
                  :key="`venue-section-${section.id}`"
                  type="button"
                  :class="['stage-venue-card', { 'is-active': isDynamicVenueSectionSelected(section) }]"
                  @click="selectDynamicVenueSectionFromMap(section)"
                >
                  <i class="stage-venue-card__swatch" :style="{ background: section.color || '#8e6bc4' }"></i>
                  <span class="stage-venue-card__copy">
                    <strong>{{ section.name }}</strong>
                    <small>{{ dynamicVenueSectionTypeLabel(section) }}</small>
                    <em v-if="dynamicVenueSectionAssignmentLabel(section)">{{ dynamicVenueSectionAssignmentLabel(section) }}</em>
                  </span>
                  <span class="stage-venue-card__check"><i class="fas fa-check"></i></span>
                </button>
              </div>
            </div>

            <div v-else class="stage-venue-empty">
              <i class="fas fa-map-location-dot"></i>
              <strong>No hay secciones en el venue</strong>
              <span>Edita el mapa en Herramientas y publica una versión con zonas.</span>
            </div>
          </div>

          <div v-else class="stage-venue-off-card">
            <i class="fas fa-circle-info"></i>
            <span>Sin sección del venue.</span>
          </div>
        </section>

        <section class="stage-modal__section stage-modal__section--optional">
          <button class="stage-optional-toggle" type="button" @click="advancedOpen = !advancedOpen">
            <span>
              <strong>Opcional</strong>
              <small>Límites, combos, fechas de disponibilidad y campos adicionales</small>
            </span>
            <i :class="advancedOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>

          <div v-if="advancedOpen" class="stage-optional-stack">
            <article class="stage-optional-card stage-optional-card--media">
              <div class="stage-modal__section-head stage-modal__section-head--compact">
                <div>
                  <span class="workspace-kicker">Imagen</span>
                  <h3>Imagen de la etapa</h3>
                </div>
                <p>Opcional. Se verá como miniatura en checkout y en la tarjeta de compra de esta etapa.</p>
              </div>

              <div class="stage-image-row">
                <div v-if="stageImagePreview" class="stage-image-preview">
                  <img :src="stageImagePreview" alt="Preview de la etapa" />
                </div>

                <div class="stage-image-controls">
                  <input ref="stageImageInput" class="stage-image-input" type="file" accept="image/*" @change="onStageImageSelected" />
                  <div class="stage-image-actions">
                    <button class="workspace-chip-button" type="button" @click="$refs.stageImageInput && $refs.stageImageInput.click()">
                      <i class="fas fa-image"></i>
                      <span>{{ stageImagePreview ? 'Cambiar imagen' : 'Agregar imagen' }}</span>
                    </button>
                    <span class="stage-image-helper">Usa una imagen liviana, clara y fácil de reconocer.</span>
                  </div>
                </div>
              </div>
            </article>

            <article class="stage-optional-card">
              <div class="stage-modal__section-head stage-modal__section-head--compact">
                <div>
                  <span class="workspace-kicker">Reglas comerciales</span>
                  <h3>Combos, fechas y límites</h3>
                </div>
                <p>Configura reglas y tiempos solo si hacen falta.</p>
              </div>

              <div class="stage-modal__grid stage-modal__grid--rules">
                <label class="workspace-field">
                  <span>Entradas por unidad</span>
                  <input v-model.number="form.quantity" class="workspace-input" type="number" min="1" required />
                </label>

                <label class="workspace-field">
                  <span>Categoría</span>
                  <select v-model.number="form.category" class="workspace-input workspace-select">
                    <option :value="1">FB / IG</option>
                    <option :value="2">Tiktok</option>
                    <option :value="3">Mail</option>
                    <option :value="4">Ambassador</option>
                    <option :value="5">Page</option>
                  </select>
                </label>

                <label class="workspace-field">
                  <span>Disponible desde</span>
                  <input v-model="form.sales_date" class="workspace-input" type="datetime-local" />
                </label>

                <label class="workspace-field">
                  <span>Disponible hasta</span>
                  <input v-model="form.expiration_date" class="workspace-input" type="datetime-local" />
                </label>

                <div class="workspace-field workspace-field--wide workspace-limit-card">
                  <div class="workspace-limit-card__header">
                    <div>
                      <span class="workspace-limit-card__title">Límite por código en esta etapa</span>
                      <small class="workspace-field__note">Decide cuántas veces puede entrar el mismo código a esta etapa privada.</small>
                    </div>
                    <label class="workspace-inline-check workspace-inline-check--compact">
                      <input v-model="codeLimitEnabled" type="checkbox" :disabled="!codeUsageLimitApplies" />
                      <span>Limitar usos</span>
                    </label>
                  </div>

                  <div class="workspace-limit-card__body">
                    <label class="workspace-field workspace-field--limit-inline">
                        <span>Máximo de usos por código</span>
                        <input
                          v-model.number="form.max_code_uses"
                          class="workspace-input"
                        type="number"
                        min="1"
                        :disabled="!codeUsageLimitApplies || !codeLimitEnabled"
                        placeholder="Ej: 3 usos"
                      />
                    </label>
                    <p class="workspace-summary-line workspace-summary-line--compact">{{ codeLimitSummary }}</p>
                  </div>
                </div>
              </div>

              <p class="workspace-summary-line">{{ comboSummary }}</p>
            </article>

            <article class="stage-optional-card stage-optional-card--fields">
              <button class="workspace-chip-button workspace-chip-button--toggle" type="button" @click="stageGuestFieldsOpen = !stageGuestFieldsOpen">
                <i :class="stageGuestFieldsOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                <span>Campos extra · {{ stageGuestFieldsSummary }}</span>
              </button>

              <div v-if="stageGuestFieldsOpen" class="workspace-list-stack">
                <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                  <div>
                    <strong>Configuracion de esta etapa</strong>
                    <p>Agrega, ordena y marca los campos que se mostraran en checkout.</p>
                  </div>
                  <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('stage')">
                    <i class="fas fa-plus"></i>
                    <span>Crear reutilizable</span>
                  </button>
                </div>

                <div v-if="catalogEditorOpen && catalogEditorContext === 'stage'" class="workspace-card workspace-card--soft">
                  <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                    <div>
                      <strong>{{ editingCatalogFieldId ? 'Editar campo reutilizable' : 'Nuevo campo reutilizable' }}</strong>
                      <p>Se crea para el evento y queda agregado de una vez a esta etapa.</p>
                    </div>
                    <button class="workspace-chip-button" type="button" @click="closeCatalogFieldEditor">
                      <i class="fas fa-times"></i>
                      <span>Cerrar</span>
                    </button>
                  </div>

                  <form class="workspace-form" @submit.prevent="submitCatalogField">
                    <label class="workspace-field">
                      <span>Etiqueta</span>
                      <input v-model="catalogForm.label" class="workspace-input" type="text" placeholder="Documento" required />
                    </label>

                    <label class="workspace-field">
                      <span>Tipo</span>
                      <select v-model="catalogForm.field_type" class="workspace-input workspace-select">
                        <option value="field">Campo libre</option>
                        <option value="multiple">Seleccion unica</option>
                      </select>
                    </label>

                    <label class="workspace-field workspace-field--wide">
                      <span>Ayuda</span>
                      <input v-model="catalogForm.help_text" class="workspace-input" type="text" placeholder="Se vera debajo del campo en checkout" />
                    </label>

                    <label v-if="catalogForm.field_type === 'multiple'" class="workspace-field workspace-field--wide">
                      <span>Opciones</span>
                      <textarea v-model="catalogOptionsDraft" class="workspace-input workspace-input--textarea" rows="3" placeholder="S&#10;M&#10;L"></textarea>
                      <small class="workspace-field__note">Una opcion por linea.</small>
                    </label>

                    <label class="workspace-inline-check workspace-inline-check--single">
                      <input v-model="catalogForm.is_active" type="checkbox" />
                      <span>Campo activo</span>
                    </label>

                    <div class="workspace-actions">
                      <button class="workspace-button workspace-button--primary" type="submit" :disabled="catalogSaving">
                        <i class="fas fa-save"></i>
                        <span>{{ catalogSaving ? 'Guardando...' : editingCatalogFieldId ? 'Guardar campo' : 'Crear campo' }}</span>
                      </button>
                    </div>
                  </form>
                </div>

                <div v-if="stageAssignmentDetails.length" class="workspace-list-stack">
                  <article v-for="(assignment, index) in stageAssignmentDetails" :key="`stage-${assignment.field_id}`" class="workspace-assignment-card">
                    <div>
                      <strong>{{ assignment.label }}</strong>
                      <p>{{ fieldTypeLabel(assignment.field_type) }}</p>
                    </div>
                    <div class="workspace-assignment-controls">
                      <label class="workspace-inline-check workspace-inline-check--single">
                        <input :checked="assignment.is_required" type="checkbox" @change="toggleAssignmentRequired(stageAssignments, assignment.field_id)" />
                        <span>Obligatorio</span>
                      </label>
                      <div class="workspace-inline-actions">
                        <button class="workspace-chip-button" type="button" :disabled="index === 0" @click="moveAssignment(stageAssignments, index, -1)">
                          <i class="fas fa-arrow-up"></i>
                        </button>
                        <button class="workspace-chip-button" type="button" :disabled="index === stageAssignmentDetails.length - 1" @click="moveAssignment(stageAssignments, index, 1)">
                          <i class="fas fa-arrow-down"></i>
                        </button>
                        <button class="workspace-chip-button" type="button" @click="toggleStageAssignment(assignment.field_id)">
                          <i class="fas fa-minus"></i>
                          <span>Quitar</span>
                        </button>
                      </div>
                    </div>
                  </article>
                </div>
                <div v-else class="workspace-empty workspace-empty--compact">Esta etapa todavia no pide datos extra.</div>

                <div class="workspace-card workspace-card--soft">
                  <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                    <div>
                      <strong>Catalogo disponible</strong>
                      <p>Reutiliza campos existentes del evento en esta etapa.</p>
                    </div>
                  </div>
                  <div v-if="!catalogFields.length" class="workspace-empty workspace-empty--compact">No hay campos en el catálogo aún.</div>
                  <div v-else class="workspace-list-stack">
                    <article v-for="field in catalogFields" :key="`stage-catalog-${field.id}`" class="workspace-assignment-card workspace-assignment-card--catalog">
                      <div>
                        <strong>{{ field.label }}</strong>
                        <p>{{ fieldTypeLabel(field.field_type) }}</p>
                      </div>
                      <div class="workspace-inline-actions">
                        <button class="workspace-chip-button" type="button" @click="toggleStageAssignment(field.id)">
                          <i :class="isFieldSelected(stageAssignments, field.id) ? 'fas fa-minus' : 'fas fa-plus'"></i>
                          <span>{{ isFieldSelected(stageAssignments, field.id) ? 'Quitar' : 'Agregar' }}</span>
                        </button>
                        <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('stage', field)">
                          <i class="fas fa-pen"></i>
                          <span>Editar</span>
                        </button>
                        <button class="workspace-chip-button" type="button" @click="deleteCatalogField(field)">
                          <i class="fas fa-trash-alt"></i>
                          <span>Eliminar</span>
                        </button>
                      </div>
                    </article>
                  </div>
                </div>

                <div class="workspace-card workspace-card--soft">
                  <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                    <div>
                      <strong>Preview checkout</strong>
                      <p>Se guarda despues de guardar la etapa.</p>
                    </div>
                  </div>
                  <div class="workspace-preview-stack">
                    <template v-if="stageAssignmentDetails.length">
                      <div v-for="field in stageAssignmentDetails" :key="`stage-preview-${field.field_id}`" class="workspace-preview-field">
                        <label>
                          {{ field.label }}
                          <span v-if="field.is_required">*</span>
                        </label>
                        <input v-if="field.field_type === 'field'" class="workspace-input" type="text" :placeholder="field.help_text || 'Respuesta del invitado'" disabled />
                        <select v-else class="workspace-input workspace-select" disabled>
                          <option value="">Selecciona una opcion</option>
                          <option v-for="option in field.options" :key="`${field.field_id}-preview-${option}`" :value="option">{{ option }}</option>
                        </select>
                        <small v-if="field.help_text" class="workspace-field__note">{{ field.help_text }}</small>
                      </div>
                    </template>
                    <div v-else class="workspace-empty workspace-empty--compact">Agrega campos para ver como quedara el checkout.</div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <div class="stage-form-actions">
          <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="resetForm">
            <i class="fas fa-rotate-left"></i>
            <span>{{ editingTicketId ? 'Cancelar edición' : 'Cancelar' }}</span>
          </button>
          <button class="workspace-button workspace-button--small workspace-button--primary" type="submit" :disabled="saving">
            <i class="fas fa-save"></i>
            <span>{{ saving ? 'Guardando...' : editingTicketId ? 'Actualizar etapa' : 'Crear etapa' }}</span>
          </button>
        </div>
      </form>
    </WorkspaceWizardModal>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Inventario</span>
          <h2 class="workspace-title">Etapas actuales</h2>
        </div>
      </div>

      <div v-if="loading && !stageTickets.length" class="workspace-empty">Cargando etapas...</div>
      <div v-else-if="!stageTickets.length" class="workspace-empty">Todavia no hay etapas creadas. Crea la primera para empezar a vender o segmentar accesos.</div>
      <div v-else class="workspace-table-shell stages-table-shell">
        <table class="workspace-table stages-table">
          <thead>
            <tr>
              <th>Etapa</th>
              <th>Precio</th>
              <th>Stock</th>
              <th>Max</th>
              <th>Combo</th>
              <th>Checkout</th>
              <th>Acceso</th>
              <th>Link</th>
              <th>Límite por código</th>
              <th>Campos</th>
              <th>Status</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in stageTickets" :key="ticket.id">
              <td class="stage-summary-cell" data-label="Etapa">
                <div class="workspace-table-title">{{ ticket.name }}</div>
                <p class="workspace-table-subcopy">{{ ticket.description || 'Sin descripción.' }}</p>
              </td>
              <td data-label="Precio">
                ${{ formatMoney(stageTicketPricing(ticket).grossAmount) }}
                <small v-if="stageTicketPricing(ticket).taxAmount > 0" class="workspace-table-subcopy">{{ ticket.tax_label || 'IVA' }} incluido</small>
              </td>
              <td data-label="Stock">{{ ticket.initial_quantity }}</td>
              <td data-label="Max">{{ ticket.max_quantity }}</td>
              <td data-label="Combo">{{ comboLabel(ticket) }}</td>
              <td data-label="Checkout">{{ stageVisibilityLabel(ticket) }}</td>
              <td data-label="Acceso">
                {{ stageCodeAccessLabel(ticket) }}
                <small v-if="dynamicVenueStageLabel(ticket)" class="workspace-table-subcopy">{{ dynamicVenueStageLabel(ticket) }}</small>
              </td>
              <td class="stage-link-cell" data-label="Link">
                <a v-if="stageLink(ticket)" class="workspace-inline-link" :href="stageLink(ticket)" target="_blank" rel="noreferrer">{{ ticket.web_experience ? 'Ver evento' : 'Ver etapa' }}</a>
                <span v-else class="workspace-table-subcopy">Sin link</span>
              </td>
              <td data-label="Límite por código">{{ codeLimitLabel(ticket) }}</td>
              <td data-label="Campos">{{ guestFieldsCountLabel(ticket) }}</td>
              <td class="stage-status-cell" data-label="Status">
                <span :class="['workspace-status-pill', ticket.status ? 'is-active' : 'is-off']">{{ ticket.status ? 'Activa' : 'Oculta' }}</span>
              </td>
              <td class="stage-actions-cell" data-label="Acciones">
                <div class="workspace-inline-actions stage-table-actions">
                  <button class="workspace-chip-button" type="button" @click="editStage(ticket)">
                    <i class="fas fa-pen"></i>
                    <span>Editar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="editStage(ticket, { openGuestFields: true })">
                    <i class="fas fa-table-list"></i>
                    <span>Campos</span>
                  </button>
                  <button v-if="stageLink(ticket)" class="workspace-chip-button" type="button" @click="copyStageLink(ticket)">
                    <i class="fas fa-link"></i>
                    <span>{{ ticket.web_experience ? 'Copiar evento' : 'Copiar link' }}</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="deleteStage(ticket)">
                    <i class="fas fa-trash-alt"></i>
                    <span>{{ ticket.status ? 'Archivar' : 'Eliminar' }}</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'
import { normalizeTicketTaxPercent, ticketUnitPricing } from '../../common/ticketTaxes.js'
import HostDynamicVenueMiniMap from '../../components/host/HostDynamicVenueMiniMap.vue'
import WorkspaceWizardModal from '../../components/workspace/WorkspaceWizardModal.vue'

const defaultForm = () => ({
  name: '',
  description: '',
  initial_quantity: 100,
  price: 0,
  tax_enabled: false,
  tax_label: 'IVA',
  tax_percent: 15,
  tax_price_includes_tax: false,
  status: true,
  web_experience: true,
  is_courtesy: false,
  type: 1,
  category: 5,
  sales_date: '',
  expiration_date: '',
  require_ig: false,
  quantity: 1,
  max_quantity: 5,
  max_code_uses: null,
  code: '',
  code_mode: 0,
  access_code_ids: [],
  dynamic_venue_mode: 'none',
  dynamic_venue_section_ids: [],
})

const defaultCatalogForm = () => ({
  label: '',
  field_type: 'field',
  help_text: '',
  is_active: true,
})

export default {
  name: 'StagesView',

  components: {
    HostDynamicVenueMiniMap,
    WorkspaceWizardModal,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      catalogSaving: false,
      applyingGlobalAssignments: false,
      tickets: [],
      codes: [],
      catalogFields: [],
      venueLayouts: [],
      loadingVenueLayouts: false,
      loadingDynamicVenue: false,
      dynamicVenueSaving: false,
      dynamicVenueControlError: '',
      dynamicVenueSelectedLayoutId: '',
      dynamicVenueState: {
        enabled: false,
        status: 'off',
        layoutVersionId: '',
        layoutId: '',
      },
      dynamicVenueLayout: null,
      dynamicVenueProtection: { has_obligations: false, sections: {}, seats: {} },
      hoveredDynamicVenueSectionId: '',
      editingTicketId: null,
      editingCatalogFieldId: null,
      codeLimitEnabled: false,
      successMessage: '',
      errorMessage: '',
      editorOpen: false,
      stageCreationPath: '',
      venueSelectionPurpose: '',
      firstStageVenuePreview: null,
      firstStageVenuePreviewLoading: false,
      firstStageVenuePreviewError: '',
      advancedOpen: false,
      stageGuestFieldsOpen: false,
      catalogWorkspaceOpen: false,
      catalogEditorOpen: false,
      catalogEditorContext: 'global',
      catalogOptionsDraft: '',
      stageCodeSearch: '',
      stageImageFile: null,
      stageImagePreview: '',
      form: defaultForm(),
      catalogForm: defaultCatalogForm(),
      stageAssignments: [],
      globalAssignments: [],
    }
  },

  computed: {
    stageTickets() {
      return this.tickets.filter((ticket) => !ticket.is_courtesy)
    },

    showFirstStageModeChooser() {
      return !this.editingTicketId && !this.stageTickets.length && !this.stageCreationPath
    },

    showFirstStageVenueSetup() {
      return !this.editingTicketId && this.stageCreationPath === 'venue-select'
    },

    stageEditorModalKicker() {
      return this.showFirstStageVenueSetup && this.venueSelectionPurpose === 'manage' ? 'Venue activo' : 'Etapas'
    },

    stageEditorModalTitle() {
      if (this.editingTicketId) return 'Editar etapa'
      if (this.showFirstStageModeChooser) return '¿Cómo quieres crear tu primera etapa?'
      if (this.showFirstStageVenueSetup && this.venueSelectionPurpose === 'manage') return 'Cambiar mapa del evento'
      if (this.showFirstStageVenueSetup && this.publishedVenueLayouts.length) return 'Elige el mapa para tu evento'
      if (this.showFirstStageVenueSetup) return 'Primero crea tu Venue'
      return 'Nueva etapa'
    },

    stageEditorModalCopy() {
      if (this.showFirstStageModeChooser) return 'Dos formas de vender, una misma experiencia FOMO.'
      if (this.showFirstStageVenueSetup && this.venueSelectionPurpose === 'manage') return 'El nuevo mapa debe conservar cualquier zona o asiento con inventario vendido o reservado.'
      if (this.showFirstStageVenueSetup && this.publishedVenueLayouts.length) return 'Selecciona un venue publicado y revísalo antes de conectarlo.'
      if (this.showFirstStageVenueSetup) return 'Tu mapa se diseña una vez y luego se reutiliza en tus eventos.'
      return ''
    },

    venueSelectorBackLabel() {
      return this.venueSelectionPurpose === 'manage' ? 'Cerrar selector' : 'Volver a las opciones'
    },

    venueSelectorConfirmLabel() {
      if (this.venueSelectionPurpose === 'manage') return this.dynamicVenueSelectedIsAssigned ? 'Mantener este mapa' : 'Cambiar a este mapa'
      return this.dynamicVenueSelectedIsAssigned ? 'Continuar con este mapa' : 'Usar este mapa'
    },

    firstStageVenuePreviewSchema() {
      const preview = this.firstStageVenuePreview || {}
      const publishedVersion = preview.published_version || {}
      return publishedVersion.schema_json || preview.schema_json || null
    },

    firstStageVenuePreviewSections() {
      const schema = this.firstStageVenuePreviewSchema
      const sections = schema && Array.isArray(schema.sections) ? schema.sections : []
      const seats = schema && Array.isArray(schema.seats) ? schema.seats : []
      return sections.map((section) => {
        const sectionSeats = seats.filter((seat) => String(seat.section_id) === String(section.id))
        const tableCount = sectionSeats.filter((seat) => seat.is_table).length
        return {
          ...section,
          has_seats: sectionSeats.length > 0,
          seat_count: sectionSeats.length - tableCount,
          table_count: tableCount,
          unit_count: sectionSeats.length,
        }
      })
    },

    firstStageVenuePreviewSeats() {
      const schema = this.firstStageVenuePreviewSchema
      return schema && Array.isArray(schema.seats) ? schema.seats : []
    },

    firstStageVenuePreviewViewBox() {
      const schema = this.firstStageVenuePreviewSchema
      return schema && schema.view_box ? schema.view_box : null
    },

    canContinueWithFirstStageVenue() {
      return Boolean(
        this.selectedDynamicVenueLayout
        && !this.firstStageVenuePreviewLoading
        && !this.firstStageVenuePreviewError
        && this.firstStageVenuePreviewSections.length
        && !this.dynamicVenueSaving
      )
    },

    firstStageVenueLoading() {
      return this.loadingVenueLayouts || this.loadingDynamicVenue
    },

    firstStageVenueStatusLabel() {
      if (this.firstStageVenueLoading) return 'Preparando mapa'
      if (this.dynamicVenueEditorAvailable) return 'Venue listo'
      if (this.publishedVenueLayouts.length) return `${this.publishedVenueLayouts.length} publicado${this.publishedVenueLayouts.length === 1 ? '' : 's'}`
      return 'Crea tu venue'
    },

    firstStageVenueDescription() {
      if (this.firstStageVenueLoading) return 'Estamos preparando tus venues para mostrarte la mejor opción.'
      if (this.dynamicVenueEditorAvailable) {
        const venueName = this.currentDynamicVenueLayout && this.currentDynamicVenueLayout.name
        return venueName ? `Conecta la etapa al mapa de ${venueName} y deja que cada persona elija su lugar.` : 'Conecta la etapa al mapa y deja que cada persona elija su lugar.'
      }
      if (this.publishedVenueLayouts.length) return 'Ya tienes un venue publicado. Actívalo en este evento para comenzar a asignar zonas.'
      return 'Dibuja zonas, mesas o asientos en Venue Studio y vuelve para conectarlos a la venta.'
    },

    firstStageVenueActionLabel() {
      if (this.firstStageVenueLoading) return 'Cargando venues'
      if (this.dynamicVenueEditorAvailable) return 'Crear con mapa'
      if (this.publishedVenueLayouts.length) return 'Activar venue'
      return 'Crear venue'
    },

    activeStages() {
      return this.stageTickets.filter((ticket) => ticket.status)
    },

    hiddenStages() {
      return this.stageTickets.filter((ticket) => !ticket.status)
    },

    isCourtesyPrice() {
      return Number(this.form.price) === 0
    },

    stageTaxPreview() {
      return ticketUnitPricing(this.form)
    },

    showStageCodeApplicability() {
      return this.activeCodes.length > 0
    },

    activeCodes() {
      return this.codes.filter((code) => code.is_active)
    },

    dynamicVenueEnabled() {
      return Boolean(
        this.dynamicVenueLayout
        && this.dynamicVenueState.enabled
        && this.dynamicVenueState.status === 'published'
        && this.dynamicVenueState.layoutVersionId
      )
    },

    publishedVenueLayouts() {
      return this.venueLayouts
        .filter((layout) => layout && layout.published_version && layout.published_version.id)
        .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))
    },

    selectedDynamicVenueLayout() {
      const selectedId = String(this.dynamicVenueSelectedLayoutId || '')
      return this.publishedVenueLayouts.find((layout) => String(layout.id) === selectedId) || null
    },

    currentDynamicVenueLayout() {
      const state = this.dynamicVenueState || {}
      const layoutId = String(state.layoutId || '')
      const versionId = String(state.layoutVersionId || '')
      return this.publishedVenueLayouts.find((layout) => {
        const publishedVersionId = layout && layout.published_version ? String(layout.published_version.id || '') : ''
        return (layoutId && String(layout.id) === layoutId) || (versionId && publishedVersionId === versionId)
      }) || null
    },

    activeDynamicVenueName() {
      return (this.currentDynamicVenueLayout && this.currentDynamicVenueLayout.name)
        || (this.dynamicVenueLayout && this.dynamicVenueLayout.name)
        || 'Venue del checkout'
    },

    activeDynamicVenueVersionLabel() {
      const version = this.currentDynamicVenueLayout && this.currentDynamicVenueLayout.published_version
      return version && version.version ? `Versión ${version.version} publicada` : 'Versión publicada'
    },

    activeVenueStudioRoute() {
      const layoutId = (this.currentDynamicVenueLayout && this.currentDynamicVenueLayout.id) || this.dynamicVenueState.layoutId
      if (!layoutId) return null
      return {
        name: 'tools-venue-studio',
        params: { layoutId },
        query: { eventId: this.event.id, eventSlug: this.event.slug },
      }
    },

    dynamicVenueControlStatusLabel() {
      if (this.loadingVenueLayouts) return 'Cargando'
      if (!this.publishedVenueLayouts.length) return 'Sin venues'
      if (this.dynamicVenueState.enabled && this.dynamicVenueState.status === 'published' && this.dynamicVenueState.layoutVersionId) return 'Activo'
      if (this.dynamicVenueState.enabled) return 'Revisar'
      return 'Apagado'
    },

    dynamicVenueControlStatusClass() {
      if (this.dynamicVenueState.enabled && this.dynamicVenueState.status === 'published' && this.dynamicVenueState.layoutVersionId) return 'is-active'
      if (this.dynamicVenueState.enabled) return 'is-warning'
      return 'is-off'
    },

    dynamicVenueSelectPlaceholder() {
      if (this.loadingVenueLayouts) return 'Cargando venues...'
      if (!this.publishedVenueLayouts.length) return 'No hay venues publicados'
      return 'Selecciona un venue publicado'
    },

    dynamicVenueControlHint() {
      if (this.loadingVenueLayouts) return 'Cargando biblioteca de venues.'
      if (!this.publishedVenueLayouts.length) return 'Publica un venue en Herramientas para activar el mapa.'
      if (this.dynamicVenueEnabled && this.currentDynamicVenueLayout) return `Activo: ${this.currentDynamicVenueLayout.name}.`
      if (this.selectedDynamicVenueLayout) return `Listo para activar: ${this.selectedDynamicVenueLayout.name}.`
      return 'El mapa visual se asigna aqui; las zonas se conectan al crear o editar etapas.'
    },

    dynamicVenueSelectedIsAssigned() {
      if (!this.selectedDynamicVenueLayout || !this.dynamicVenueState.layoutVersionId || this.dynamicVenueState.status !== 'published') return false
      const selectedVersionId = String(this.selectedDynamicVenueLayout.published_version.id || '')
      return selectedVersionId === String(this.dynamicVenueState.layoutVersionId || '')
    },

    canAssignDynamicVenueLayout() {
      return Boolean(
        !this.dynamicVenueSaving
        && !this.loadingVenueLayouts
        && this.selectedDynamicVenueLayout
        && !this.dynamicVenueSelectedIsAssigned
      )
    },

    dynamicVenueAssignButtonLabel() {
      if (this.dynamicVenueSaving) return 'Guardando...'
      if (this.dynamicVenueSelectedIsAssigned) return 'Mapa activo'
      if (this.dynamicVenueState.enabled) return 'Cambiar mapa'
      return 'Activar mapa'
    },

    dynamicVenueEditorAvailable() {
      return Boolean(this.dynamicVenueEnabled && this.dynamicVenueLayout && this.dynamicVenueSections.length)
    },

    dynamicVenueSections() {
      return this.dynamicVenueLayout && Array.isArray(this.dynamicVenueLayout.sections) ? this.dynamicVenueLayout.sections : []
    },

    dynamicVenueSeats() {
      return this.dynamicVenueLayout && Array.isArray(this.dynamicVenueLayout.seats) ? this.dynamicVenueLayout.seats : []
    },

    dynamicVenueObjects() {
      return this.dynamicVenueLayout && Array.isArray(this.dynamicVenueLayout.objects) ? this.dynamicVenueLayout.objects : []
    },

    dynamicVenueVisibleSections() {
      if (this.form.dynamic_venue_mode === 'none') return []
      return this.dynamicVenueSections
    },

    dynamicVenueMapSections() {
      return this.dynamicVenueVisibleSections.filter((section) => !this.dynamicVenueSectionIsHidden(section))
    },

    dynamicVenueMapHitSections() {
      return [...this.dynamicVenueMapSections].sort((a, b) => this.dynamicVenueSectionArea(b) - this.dynamicVenueSectionArea(a))
    },

    dynamicVenueRenderItems() {
      const items = []
      this.dynamicVenueMapSections.forEach((section, index) => {
        items.push({ type: 'section', key: `section:${section.id}`, element: section, zIndex: this.dynamicVenueLayerOrder(section, 'section', index), sourceIndex: index })
      })
      this.dynamicVenueObjects.forEach((object, index) => {
        items.push({ type: 'object', key: `object:${object.id}`, element: object, zIndex: this.dynamicVenueLayerOrder(object, 'object', index), sourceIndex: 10000 + index })
      })
      this.dynamicVenueSeats.forEach((seat, index) => {
        items.push({ type: 'seat', key: `seat:${seat.key}`, element: seat, zIndex: this.dynamicVenueLayerOrder(seat, 'seat', index), sourceIndex: 20000 + index })
      })
      return items.sort((left, right) => (left.zIndex - right.zIndex) || (left.sourceIndex - right.sourceIndex) || left.key.localeCompare(right.key))
    },

    dynamicVenueMapViewBox() {
      const box = this.dynamicVenueViewBox || {}
      return {
        x: Number(box.x) || 0,
        y: Number(box.y) || 0,
        width: Math.max(Number(box.width) || 1400, 1),
        height: Math.max(Number(box.height) || 900, 1),
      }
    },

    dynamicVenueViewBoxString() {
      const box = this.dynamicVenueMapViewBox
      return `${box.x} ${box.y} ${box.width} ${box.height}`
    },

    selectedDynamicVenueSections() {
      const ids = Array.isArray(this.form.dynamic_venue_section_ids) ? this.form.dynamic_venue_section_ids : []
      return ids
        .map((sectionId) => this.dynamicVenueSections.find((section) => String(section.id) === String(sectionId)))
        .filter(Boolean)
    },

    dynamicVenueStageAssignments() {
      return this.stageTickets.reduce((result, ticket) => {
        const mode = ticket.dynamic_venue_mode || 'none'
        const sectionIds = Array.isArray(ticket.dynamic_venue_section_ids) ? ticket.dynamic_venue_section_ids : []
        if (mode === 'none' || !sectionIds.length) return result
        sectionIds.forEach((sectionId) => {
          if (!result[sectionId]) result[sectionId] = []
          result[sectionId].push({
            id: ticket.id,
            name: ticket.name,
            mode,
            quantity: Number(ticket.quantity) || 1,
          })
        })
        return result
      }, {})
    },

    dynamicVenueViewBox() {
      return this.dynamicVenueLayout ? this.dynamicVenueLayout.view_box : null
    },

    dynamicVenueSelectionSummary() {
      if (this.form.dynamic_venue_mode === 'none') {
        return 'Sin sección asignada.'
      }

      if (!this.selectedDynamicVenueSections.length) {
        return 'Pendiente de sección.'
      }

      const names = this.selectedDynamicVenueSections.map((section) => section.name).filter(Boolean)
      if (names.length > 2) return `Conectada a ${names.slice(0, 2).join(', ')} +${names.length - 2}.`
      return `Conectada a ${names.join(', ')}.`
    },

    dynamicVenuePickerTitle() {
      return 'Selecciona en el mapa'
    },

    dynamicVenuePickerHint() {
      return 'Click sobre la zona, asiento o mesa que corresponde a esta etapa.'
    },

    dynamicVenueModeHelper() {
      if (this.form.dynamic_venue_mode === 'seat') return 'Unidad exacta: butacas o mesas.'
      if (this.form.dynamic_venue_mode === 'zone') return 'Cantidad por unidad. Sin asiento numerado.'
      return 'Checkout normal.'
    },

    comboSummary() {
      const units = Number(this.form.quantity) || 1
      const max = Number(this.form.max_quantity) || 1
      const total = units * max
      return units > 1
        ? `Cada unidad entrega ${units} entradas. El maximo por compra permite hasta ${total} accesos en una sola orden.`
        : `Cada unidad entrega una entrada. El maximo por compra permite hasta ${max} accesos en una sola orden.`
    },

    codeUsageLimitApplies() {
      return !this.form.web_experience && (this.stageCodeAvailability === 'all' || this.stageCodeAvailability === 'selected')
    },

    codeLimitSummary() {
      if (!this.codeUsageLimitApplies) {
        return 'No aplica aquí: esta etapa está visible en home o no depende de códigos.'
      }

      if (!this.codeLimitEnabled || !this.form.max_code_uses) {
        return 'Cada código puede entrar sin límite a esta etapa privada.'
      }

      return `Un mismo código podrá entrar hasta ${this.form.max_code_uses} veces a esta etapa.`
    },

    stageAssignmentDetails() {
      return this.enrichAssignments(this.stageAssignments)
    },

    globalAssignmentDetails() {
      return this.enrichAssignments(this.globalAssignments)
    },

    stageGuestFieldsSummary() {
      const count = this.stageAssignmentDetails.length
      if (!count) {
        return 'Sin campos'
      }
      return `${count} configurado${count === 1 ? '' : 's'}`
    },

    stageCheckoutVisibility: {
      get() {
        return this.form.web_experience ? 'checkout' : 'hidden'
      },
      set(value) {
        this.form.web_experience = value === 'checkout'
      },
    },

    stageCodeAvailability: {
      get() {
        if (!this.activeCodes.length) {
          return 'none'
        }

        if (Number(this.form.code_mode) === 1) {
          return 'all'
        }

        if (Number(this.form.code_mode) === 2) {
          if (Array.isArray(this.form.access_code_ids) && this.form.access_code_ids.length) {
            return 'selected'
          }

          if (this.autoPrivateLinkActive) {
            return 'none'
          }

          return 'selected'
        }

        return 'none'
      },
      set(value) {
        if (value === 'all') {
          this.form.code_mode = 1
          this.form.access_code_ids = []
          this.form.code = ''
          return
        }

        if (value === 'selected') {
          this.form.code_mode = 2
          this.form.code = ''
          return
        }

        this.form.code_mode = 0
        this.form.access_code_ids = []
        this.form.code = ''
        this.codeLimitEnabled = false
      },
    },

    autoPrivateLinkActive() {
      return !this.form.web_experience
        && Number(this.form.code_mode) === 2
        && (!Array.isArray(this.form.access_code_ids) || !this.form.access_code_ids.length)
        && Boolean(this.form.code)
        && String(this.form.code).trim().toUpperCase().startsWith('STAGE-')
    },

    filteredActiveCodes() {
      const term = String(this.stageCodeSearch || '').trim().toLowerCase()
      if (!term) {
        return this.activeCodes
      }

      return this.activeCodes.filter((code) => {
        const haystack = `${code.code || ''} ${code.label || ''}`.toLowerCase()
        return haystack.includes(term)
      })
    },

    selectedCodeDetails() {
      return (Array.isArray(this.form.access_code_ids) ? this.form.access_code_ids : [])
        .map((codeId) => this.activeCodes.find((code) => Number(code.id) === Number(codeId)))
        .filter(Boolean)
    },

    optionalRuleCount() {
      let count = 0

      if ((Number(this.form.quantity) || 1) > 1) count += 1
      if ((Number(this.form.type) || 1) !== 1) count += 1
      if ((Number(this.form.category) || 5) !== 5) count += 1
      if (this.form.sales_date) count += 1
      if (this.form.expiration_date) count += 1
      if (this.codeLimitEnabled && this.form.max_code_uses) count += 1

      return count
    },

    stageOptionalSummary() {
      const facts = []
      if (this.optionalRuleCount) {
        facts.push(`${this.optionalRuleCount} regla${this.optionalRuleCount === 1 ? '' : 's'}`)
      }
      if (this.stageAssignmentDetails.length) {
        const count = this.stageAssignmentDetails.length
        facts.push(`${count} campo${count === 1 ? '' : 's'}`)
      }

      return facts.length ? facts.join(' · ') : 'Sin extras activos'
    },

    stageVisibilityNarrative() {
      if (this.stageCheckoutVisibility === 'checkout') {
        if (this.stageCodeAvailability === 'selected') {
          const count = this.selectedCodeDetails.length
          return count
            ? `Visible en checkout. También acepta ${count} código${count === 1 ? '' : 's'} elegido${count === 1 ? '' : 's'}.`
            : 'Visible en checkout. Falta elegir los códigos.'
        }

        if (this.stageCodeAvailability === 'all') {
          return 'Visible en checkout. También responde a cualquier código activo.'
        }

        return 'Visible en checkout.'
      }

      if (this.stageCodeAvailability === 'none') {
        return 'Oculta en checkout. Al guardar se crea un link privado interno para abrirla desde la tabla.'
      }

      if (this.stageCodeAvailability === 'selected') {
        const count = this.selectedCodeDetails.length
        return count
          ? `Oculta en checkout. Solo entra con ${count} código${count === 1 ? '' : 's'} elegido${count === 1 ? '' : 's'}.`
          : 'Oculta en checkout. Falta elegir los códigos.'
      }

      if (this.stageCodeAvailability === 'all') {
        return 'Oculta en checkout. Entra con cualquier código activo.'
      }

      return 'Oculta en checkout.'
    },
  },

  created() {
    this.fetchAll()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.fetchAll()
      },
    },

    codeUsageLimitApplies(value) {
      if (!value) {
        this.codeLimitEnabled = false
        this.form.max_code_uses = null
      }
    },

    codeLimitEnabled(value) {
      if (!value) {
        this.form.max_code_uses = null
      }
    },
  },

  methods: {
    onStageImageSelected(event) {
      const file = event && event.target && event.target.files && event.target.files[0]
      if (!file) {
        return
      }

      if (!String(file.type || '').startsWith('image/')) {
        this.resetMessages()
        this.errorMessage = 'La imagen de la etapa debe ser un archivo válido.'
        if (this.$refs.stageImageInput) {
          this.$refs.stageImageInput.value = ''
        }
        return
      }

      this.stageImageFile = file
      this.stageImagePreview = window.URL.createObjectURL(file)
    },

    uploadStageImage(ticketId) {
      if (!this.stageImageFile) {
        return Promise.resolve({ ok: true })
      }

      const payload = new FormData()
      payload.append('generic_image', this.stageImageFile)

      return apiServiceForm(`/api/host/events/tickets/${ticketId}/`, 'PATCH', payload)
        .then((response) => {
          if (!response || response.err) {
            return { ok: false, err: (response && response.err) || 'La etapa se guardó, pero no pudimos subir su imagen.' }
          }
          return { ok: true, data: response }
        })
    },

    generateStagePrivateCode() {
      const current = String(this.form.code || '').trim().toUpperCase()
      if (current && current.startsWith('STAGE-')) {
        return current
      }

      const base = String(this.form.name || 'stage')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toUpperCase()
        .slice(0, 12) || 'STAGE'

      return `STAGE-${base}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
    },

    stagePrimaryCode(ticket) {
      if (Array.isArray(ticket.access_codes_detail) && ticket.access_codes_detail.length) {
        return ticket.access_codes_detail[0].code
      }

      return ticket.code || ''
    },

    stageLink(ticket) {
      const eventBase = `${window.location.origin}/event/${this.event.slug}`
      const base = `${eventBase}?stage=${ticket.id}`
      const code = this.stagePrimaryCode(ticket)

      if (ticket.web_experience) {
        return eventBase
      }

      if (code) {
        return `${base}&co=${encodeURIComponent(code)}&wanted=true`
      }

      if (!ticket.web_experience && Number(ticket.code_mode) === 0) {
        return base
      }

      return ''
    },

    copyStageLink(ticket) {
      const link = this.stageLink(ticket)
      this.resetMessages()

      if (!link) {
        this.errorMessage = 'Esta etapa no tiene un link directo todavia.'
        return
      }

      navigator.clipboard.writeText(link).then(() => {
        this.successMessage = `Link de ${ticket.name} copiado.`
      }).catch(() => {
        this.errorMessage = 'No se pudo copiar el link.'
      })
    },

    toggleStageCodeSelection(codeId) {
      const nextIds = Array.isArray(this.form.access_code_ids) ? [...this.form.access_code_ids] : []
      const normalizedId = Number(codeId)
      const index = nextIds.findIndex((item) => Number(item) === normalizedId)

      if (index >= 0) {
        nextIds.splice(index, 1)
      } else {
        nextIds.push(normalizedId)
      }

      this.form.access_code_ids = nextIds
      this.stageCodeAvailability = 'selected'
    },

    normalizeDynamicVenueKey(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
    },

    dynamicVenueSectionMode(section) {
      return section && (section.kind === 'zone' || section.selection_mode === 'zone') ? 'zone' : 'seat'
    },

    dynamicVenueSectionIsHidden(section) {
      return Boolean(section && (section.visual_mode === 'hidden' || section.hidden || section.is_hidden))
    },

    dynamicVenueSectionShape(section) {
      const shape = section && section.shape && typeof section.shape === 'object' ? section.shape : {}
      return {
        ...shape,
        x: Number(shape.x) || 0,
        y: Number(shape.y) || 0,
        w: Math.max(Number(shape.w) || 1, 1),
        h: Math.max(Number(shape.h) || 1, 1),
        rx: Number(shape.rx) || 24,
      }
    },

    dynamicVenueSectionShapeType(section) {
      const shape = this.dynamicVenueSectionShape(section)
      return ['ellipse', 'polygon'].includes(shape.type) ? shape.type : 'rect'
    },

    dynamicVenueNormalizeSectionArea(area) {
      const source = area && typeof area === 'object' ? area : {}
      const path = String(source.d || source.path || '').trim()
      if (path) {
        return {
          ...source,
          type: 'path',
          d: path,
          fill_rule: String(source.fill_rule || source.fillRule || 'evenodd'),
          x: Number(source.x) || 0,
          y: Number(source.y) || 0,
          w: Math.max(Number(source.w || source.width) || 1, 1),
          h: Math.max(Number(source.h || source.height) || 1, 1),
        }
      }

      const type = ['ellipse', 'polygon'].includes(source.type) ? source.type : 'rect'
      return {
        ...source,
        type,
        x: Number(source.x) || 0,
        y: Number(source.y) || 0,
        w: Math.max(Number(source.w || source.width) || 1, 1),
        h: Math.max(Number(source.h || source.height) || 1, 1),
        rx: Number(source.rx) || 24,
      }
    },

    dynamicVenueSectionVisualAreas(section) {
      const rawAreas = Array.isArray(section?.areas) ? section.areas : []
      const areas = rawAreas
        .map((area, index) => ({
          key: `${section?.id || 'section'}-area-${area?.id || index}`,
          shape: this.dynamicVenueNormalizeSectionArea(area?.shape || area),
        }))
        .filter((area) => area.shape && area.shape.w > 0 && area.shape.h > 0)
      return areas.length ? areas : [{ key: `${section?.id || 'section'}-shape`, shape: this.dynamicVenueSectionShape(section) }]
    },

    dynamicVenueSectionAreaShapeType(area) {
      return ['path', 'ellipse', 'polygon'].includes(area?.shape?.type) ? area.shape.type : 'rect'
    },

    dynamicVenueSectionAreaPath(area) {
      return String(area?.shape?.d || '')
    },

    dynamicVenueSectionAreaFillRule(area) {
      return String(area?.shape?.fill_rule || area?.shape?.fillRule || 'evenodd')
    },

    dynamicVenueSectionAreaCenterX(area) {
      return (Number(area?.shape?.x) || 0) + ((Number(area?.shape?.w) || 0) / 2)
    },

    dynamicVenueSectionAreaCenterY(area) {
      return (Number(area?.shape?.y) || 0) + ((Number(area?.shape?.h) || 0) / 2)
    },

    dynamicVenueSectionAreaPolygonPoints(area) {
      const shape = area?.shape || {}
      const points = Array.isArray(shape.points) ? shape.points : []
      if (points.length >= 3) {
        return points.map((point) => `${Number(point.x) || 0},${Number(point.y) || 0}`).join(' ')
      }
      return `${shape.x},${shape.y} ${shape.x + shape.w},${shape.y} ${shape.x + shape.w},${shape.y + shape.h} ${shape.x},${shape.y + shape.h}`
    },

    dynamicVenueSectionPoints(section) {
      const shape = this.dynamicVenueSectionShape(section)
      const points = Array.isArray(shape.points) ? shape.points : []
      if (points.length >= 3) {
        return points.map((point) => `${Number(point.x) || 0},${Number(point.y) || 0}`).join(' ')
      }
      return `${shape.x},${shape.y} ${shape.x + shape.w},${shape.y} ${shape.x + shape.w},${shape.y + shape.h} ${shape.x},${shape.y + shape.h}`
    },

    dynamicVenueSectionArea(section) {
      return this.dynamicVenueSectionVisualAreas(section).reduce((sum, area) => {
        const shape = area.shape || {}
        const points = Array.isArray(shape.points) ? shape.points : []
        if (this.dynamicVenueSectionAreaShapeType(area) !== 'polygon' || points.length < 3) {
          return sum + ((Number(shape.w) || 0) * (Number(shape.h) || 0))
        }
        const polygonArea = points.reduce((total, point, index) => {
          const next = points[(index + 1) % points.length]
          return total + ((Number(point.x) || 0) * (Number(next.y) || 0)) - ((Number(next.x) || 0) * (Number(point.y) || 0))
        }, 0)
        return sum + Math.abs(polygonArea / 2)
      }, 0)
    },

    dynamicVenueSvgPoint(event) {
      const svg = event && event.currentTarget
      if (!svg || typeof svg.createSVGPoint !== 'function' || !svg.getScreenCTM()) return null
      const point = svg.createSVGPoint()
      point.x = event.clientX
      point.y = event.clientY
      return point.matrixTransform(svg.getScreenCTM().inverse())
    },

    dynamicVenueSectionContainsPoint(section, point) {
      if (!section || !point) return false
      return this.dynamicVenueSectionVisualAreas(section).some((area) => this.dynamicVenueSectionAreaContainsPoint(area, point))
    },

    dynamicVenueSectionAreaContainsPoint(area, point) {
      if (!area || !point) return false
      const shape = area.shape || {}
      const type = this.dynamicVenueSectionAreaShapeType(area)
      if (type === 'path') return this.dynamicVenuePathContainsPoint(area, point)
      if (type === 'ellipse') {
        const radiusX = shape.w / 2
        const radiusY = shape.h / 2
        if (!radiusX || !radiusY) return false
        const dx = (point.x - (shape.x + radiusX)) / radiusX
        const dy = (point.y - (shape.y + radiusY)) / radiusY
        return (dx * dx) + (dy * dy) <= 1
      }
      if (type === 'polygon') {
        const points = Array.isArray(shape.points) ? shape.points : []
        return this.dynamicVenuePointInPolygon(point, points.length >= 3 ? points : [
          { x: shape.x, y: shape.y },
          { x: shape.x + shape.w, y: shape.y },
          { x: shape.x + shape.w, y: shape.y + shape.h },
          { x: shape.x, y: shape.y + shape.h },
        ])
      }
      return point.x >= shape.x && point.x <= shape.x + shape.w && point.y >= shape.y && point.y <= shape.y + shape.h
    },

    dynamicVenuePathContainsPoint(area, point) {
      const shape = area?.shape || {}
      const fallback = point.x >= shape.x && point.x <= shape.x + shape.w && point.y >= shape.y && point.y <= shape.y + shape.h
      if (typeof document === 'undefined' || typeof document.createElementNS !== 'function') return fallback
      try {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        path.setAttribute('d', this.dynamicVenueSectionAreaPath(area))
        path.setAttribute('fill-rule', this.dynamicVenueSectionAreaFillRule(area))
        if (typeof path.isPointInFill !== 'function') return fallback
        const svgPoint = typeof window !== 'undefined' && typeof window.DOMPoint === 'function' ? new window.DOMPoint(point.x, point.y) : { x: point.x, y: point.y }
        return path.isPointInFill(svgPoint)
      } catch (_error) {
        return fallback
      }
    },

    dynamicVenuePointInPolygon(point, points) {
      if (!point || !Array.isArray(points) || points.length < 3) return false
      let inside = false
      for (let index = 0, previous = points.length - 1; index < points.length; previous = index, index += 1) {
        const currentPoint = points[index]
        const previousPoint = points[previous]
        const currentX = Number(currentPoint.x) || 0
        const currentY = Number(currentPoint.y) || 0
        const previousX = Number(previousPoint.x) || 0
        const previousY = Number(previousPoint.y) || 0
        const intersects = ((currentY > point.y) !== (previousY > point.y))
          && (point.x < ((previousX - currentX) * (point.y - currentY)) / ((previousY - currentY) || 1) + currentX)
        if (intersects) inside = !inside
      }
      return inside
    },

    handleDynamicVenueMapClick(event) {
      const point = this.dynamicVenueSvgPoint(event)
      if (!point) return
      const section = this.dynamicVenueMapSections
        .filter((item) => this.dynamicVenueSectionContainsPoint(item, point))
        .sort((a, b) => this.dynamicVenueSectionArea(a) - this.dynamicVenueSectionArea(b))[0]
      if (section) this.selectDynamicVenueSectionFromMap(section)
    },

    dynamicVenueLayerOrder(element, type, index = 0) {
      const value = Number(element?.z_index ?? element?.zIndex)
      if (Number.isFinite(value)) return value
      if (type === 'object') return 2000 + index
      if (type === 'seat') return 3000 + index
      return 1000 + index
    },

    dynamicVenueDepthStyle(element, type = 'object') {
      const fallbackElevation = type === 'seat' ? 3 : (type === 'object' ? 2 : 1)
      const elevation = Math.max(Math.min(Number(element?.elevation ?? fallbackElevation) || fallbackElevation, 5), 0)
      const shadow = String(element?.shadow || 'soft').toLowerCase()
      const opacity = Number(element?.opacity)
      const style = {}
      if (Number.isFinite(opacity)) style.opacity = Math.max(Math.min(opacity, 1), 0.08)
      if (shadow !== 'none' && elevation > 0) {
        const y = 4 + (elevation * 5)
        const blur = 10 + (elevation * 9)
        const alpha = shadow === 'strong' ? 0.38 : 0.18 + (elevation * 0.035)
        style.filter = `drop-shadow(0 ${y}px ${blur}px rgba(0, 0, 0, ${Math.min(alpha, 0.42)}))`
      }
      return style
    },

    dynamicVenueSectionCenterX(section) {
      const shape = this.dynamicVenueSectionShape(section)
      return shape.x + (shape.w / 2)
    },

    dynamicVenueSectionCenterY(section) {
      const shape = this.dynamicVenueSectionShape(section)
      return shape.y + (shape.h / 2)
    },

    dynamicVenueSectionFill(section) {
      const color = section && section.color ? section.color : '#8e6bc4'
      const configuredAlpha = Number(section?.fill_alpha ?? section?.fillAlpha)
      const fallbackAlpha = this.isDynamicVenueSectionSelected(section) ? 0.4 : 0.14
      const alpha = Number.isFinite(configuredAlpha) ? Math.max(Math.min(configuredAlpha, 1), 0) : fallbackAlpha
      if (/^#[0-9a-f]{6}$/i.test(color)) {
        const red = Number.parseInt(color.slice(1, 3), 16)
        const green = Number.parseInt(color.slice(3, 5), 16)
        const blue = Number.parseInt(color.slice(5, 7), 16)
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`
      }
      return color
    },

    dynamicVenueObjectBox(object) {
      const source = object && typeof object === 'object' ? object : {}
      const shape = source.shape && typeof source.shape === 'object' ? source.shape : source
      return {
        x: Number(shape.x) || 0,
        y: Number(shape.y) || 0,
        w: Math.max(Number(shape.w || shape.width) || 80, 1),
        h: Math.max(Number(shape.h || shape.height) || 44, 1),
        rx: Math.max(Number(shape.rx) || 12, 0),
      }
    },

    dynamicVenueObjectShapeType(object) {
      const explicitType = String(object?.shape_type || object?.shapeType || '').toLowerCase()
      if (['ellipse', 'line', 'arrow', 'text'].includes(explicitType)) return explicitType
      const type = String(object?.type || '').toLowerCase()
      if (['column', 'circle', 'figure-circle'].includes(type)) return 'ellipse'
      if (['line', 'separator', 'figure-line', 'figure-separator'].includes(type)) return 'line'
      if (['arrow', 'figure-arrow'].includes(type)) return 'arrow'
      if (['text', 'label', 'figure-text', 'figure-label'].includes(type)) return 'text'
      return 'rect'
    },

    dynamicVenueObjectSemanticType(object) {
      const type = String(object?.type || object?.semantic_type || object?.preset || '').toLowerCase()
      if (['stage', 'bathroom'].includes(type)) return type
      return ''
    },

    dynamicVenueObjectIsSemantic(object) {
      return Boolean(this.dynamicVenueObjectSemanticType(object))
    },

    dynamicVenueObjectVariant(object) {
      return String(object?.variant || object?.symbol_variant || '').toLowerCase()
    },

    dynamicVenueStageVariant(object) {
      const variant = this.dynamicVenueObjectVariant(object)
      return ['front', 'arena', 'runway', 'corner'].includes(variant) ? variant : 'front'
    },

    dynamicVenueBathroomVariant(object) {
      const variant = this.dynamicVenueObjectVariant(object)
      return ['wc', 'women', 'men', 'accessible'].includes(variant) ? variant : 'wc'
    },

    dynamicVenueBathroomText(object) {
      const variant = this.dynamicVenueBathroomVariant(object)
      if (variant === 'women') return 'D'
      if (variant === 'men') return 'H'
      if (variant === 'accessible') return 'ACC'
      return 'WC'
    },

    dynamicVenueStageDeckPoints(object) {
      const box = this.dynamicVenueObjectBox(object)
      if (this.dynamicVenueStageVariant(object) === 'corner') {
        return `${box.x},${box.y} ${box.x + box.w},${box.y} ${box.x + box.w},${box.y + box.h * 0.55} ${box.x + box.w * 0.55},${box.y + box.h} ${box.x},${box.y + box.h}`
      }
      return `${box.x + box.w * 0.12},${box.y + box.h * 0.78} ${box.x + box.w * 0.24},${box.y + box.h * 0.24} ${box.x + box.w * 0.76},${box.y + box.h * 0.24} ${box.x + box.w * 0.88},${box.y + box.h * 0.78}`
    },

    dynamicVenueStageLinePath(object) {
      const box = this.dynamicVenueObjectBox(object)
      if (this.dynamicVenueStageVariant(object) === 'corner') {
        return `M ${box.x + box.w * 0.18} ${box.y + box.h * 0.26} H ${box.x + box.w * 0.72} V ${box.y + box.h * 0.78} M ${box.x + box.w * 0.2} ${box.y + box.h * 0.78} L ${box.x + box.w * 0.74} ${box.y + box.h * 0.24}`
      }
      return `M ${box.x + box.w * 0.16} ${box.y + box.h * 0.32} H ${box.x + box.w * 0.84} M ${box.x + box.w * 0.5} ${box.y + box.h * 0.46} V ${box.y + box.h * 0.84}`
    },

    dynamicVenueBathroomAccessiblePath(object) {
      const box = this.dynamicVenueObjectBox(object)
      return `M ${box.x + box.w * 0.38} ${box.y + box.h * 0.38} V ${box.y + box.h * 0.58} H ${box.x + box.w * 0.5} M ${box.x + box.w * 0.38} ${box.y + box.h * 0.58} L ${box.x + box.w * 0.54} ${box.y + box.h * 0.82} M ${box.x + box.w * 0.24} ${box.y + box.h * 0.67} C ${box.x + box.w * 0.24} ${box.y + box.h * 0.84} ${box.x + box.w * 0.46} ${box.y + box.h * 0.84} ${box.x + box.w * 0.46} ${box.y + box.h * 0.67}`
    },

    dynamicVenueObjectColor(object) {
      return object?.color || (object?.type === 'entrance' || object?.type === 'exit' ? '#73f5a5' : '#dce5fb')
    },

    dynamicVenueObjectFill(object) {
      const color = this.dynamicVenueObjectColor(object)
      const configuredAlpha = Number(object?.fill_alpha ?? object?.fillAlpha)
      const alpha = Number.isFinite(configuredAlpha) ? Math.max(Math.min(configuredAlpha, 1), 0) : 0.13
      if (/^#[0-9a-f]{6}$/i.test(color)) {
        const red = Number.parseInt(color.slice(1, 3), 16)
        const green = Number.parseInt(color.slice(3, 5), 16)
        const blue = Number.parseInt(color.slice(5, 7), 16)
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`
      }
      return `rgba(220, 229, 251, ${alpha})`
    },

    dynamicVenueObjectFontSize(object) {
      const size = Number(object?.font_size || object?.fontSize)
      return Number.isFinite(size) && size > 0 ? `${size}px` : null
    },

    dynamicVenueObjectLetterSpacing(object) {
      const spacing = object?.letter_spacing || object?.letterSpacing
      return spacing != null ? String(spacing) : null
    },

    dynamicVenueObjectFontWeight(object) {
      const weight = Number(object?.font_weight || object?.fontWeight)
      return Number.isFinite(weight) && weight > 0 ? String(weight) : null
    },

    dynamicVenueObjectTextStrokeWidth(object) {
      const width = Number(object?.text_stroke_width ?? object?.stroke_width ?? object?.strokeWidth)
      return Number.isFinite(width) && width >= 0 ? `${width}px` : null
    },

    dynamicVenueObjectTextStrokeColor(object) {
      const color = object?.text_stroke_color || object?.textStrokeColor || object?.stroke_color || object?.strokeColor
      return color != null ? String(color) : null
    },

    dynamicVenueObjectTextStyle(object) {
      return {
        fill: this.dynamicVenueObjectColor(object),
        fontSize: this.dynamicVenueObjectFontSize(object),
        letterSpacing: this.dynamicVenueObjectLetterSpacing(object),
        fontWeight: this.dynamicVenueObjectFontWeight(object),
        stroke: this.dynamicVenueObjectTextStrokeColor(object),
        strokeWidth: this.dynamicVenueObjectTextStrokeWidth(object),
      }
    },

    dynamicVenueSeatWidth(seat) {
      return Math.max(Number(seat?.w) || (seat?.is_table ? 76 : 34), 1)
    },

    dynamicVenueSeatHeight(seat) {
      return Math.max(Number(seat?.h) || (seat?.is_table ? 64 : 26), 1)
    },

    dynamicVenueSeatTransform(seat) {
      const rotation = Number(seat?.rotation) || 0
      const width = this.dynamicVenueSeatWidth(seat)
      const height = this.dynamicVenueSeatHeight(seat)
      return `translate(${Number(seat?.x) || 0} ${Number(seat?.y) || 0}) rotate(${rotation} ${width / 2} ${height / 2})`
    },

    dynamicVenueSeatSection(seat) {
      return this.dynamicVenueSections.find((section) => String(section.id) === String(seat?.section_id)) || null
    },

    dynamicVenueSeatSectionIsHidden(seat) {
      return this.dynamicVenueSectionIsHidden(this.dynamicVenueSeatSection(seat))
    },

    dynamicVenueSectionUnits(section) {
      const sectionId = section && section.id
      const seats = this.dynamicVenueSeats.filter((seat) => String(seat.section_id) === String(sectionId))
      const tableCount = seats.filter((seat) => seat.is_table).length
      return {
        seats: seats.length - tableCount,
        tables: tableCount,
        total: seats.length,
      }
    },

    dynamicVenueSectionTypeLabel(section) {
      if (this.dynamicVenueSectionIsHidden(section)) {
        const units = this.dynamicVenueSectionUnits(section)
        if (units.tables > 0) return `Grupo suelto · ${units.tables} mesa${units.tables === 1 ? '' : 's'}`
        if (units.seats > 0) return `Grupo suelto · ${units.seats} asiento${units.seats === 1 ? '' : 's'}`
        return 'Grupo suelto invisible'
      }
      if (this.dynamicVenueSectionMode(section) === 'zone') {
        return 'Zona sin asiento numerado'
      }

      const units = this.dynamicVenueSectionUnits(section)
      const labels = []
      if (units.seats > 0) labels.push(`${units.seats} asiento${units.seats === 1 ? '' : 's'}`)
      if (units.tables > 0) labels.push(`${units.tables} mesa${units.tables === 1 ? '' : 's'}`)
      return labels.length ? labels.join(' · ') : 'Sin unidades reservables'
    },

    dynamicVenueSectionShortTypeLabel(section) {
      if (this.dynamicVenueSectionIsHidden(section)) return 'Suelto'
      return this.dynamicVenueSectionMode(section) === 'zone' ? 'Zona' : 'Asiento/mesa'
    },

    dynamicVenueSectionAssignmentsFor(section) {
      const assignments = this.dynamicVenueStageAssignments[section.id] || []
      return assignments.filter((assignment) => Number(assignment.id) !== Number(this.editingTicketId))
    },

    dynamicVenueSectionAssignmentLabel(section) {
      const assignments = this.dynamicVenueSectionAssignmentsFor(section)
      if (!assignments.length) return ''
      if (assignments.length === 1) return `Tambien usa: ${assignments[0].name}`
      return `${assignments.length} entradas ya usan esta sección`
    },

    isDynamicVenueSectionSelected(section) {
      return Array.isArray(this.form.dynamic_venue_section_ids)
        && this.form.dynamic_venue_section_ids.some((sectionId) => String(sectionId) === String(section.id))
    },

    isDynamicVenueSectionHovered(section) {
      return Boolean(section && this.hoveredDynamicVenueSectionId && String(this.hoveredDynamicVenueSectionId) === String(section.id))
    },

    hoverDynamicVenueSection(section) {
      this.hoveredDynamicVenueSectionId = section && section.id ? String(section.id) : ''
    },

    clearDynamicVenueSectionHover(section) {
      if (!section || String(this.hoveredDynamicVenueSectionId) === String(section.id)) this.hoveredDynamicVenueSectionId = ''
    },

    isDynamicVenueSectionIdSelected(sectionId) {
      return Array.isArray(this.form.dynamic_venue_section_ids)
        && this.form.dynamic_venue_section_ids.some((selectedId) => String(selectedId) === String(sectionId))
    },

    selectDynamicVenueSectionById(sectionId) {
      const section = this.dynamicVenueSections.find((item) => String(item.id) === String(sectionId))
      if (section) this.selectDynamicVenueSectionFromMap(section)
    },

    toggleDynamicVenueSection(section) {
      if (!section || this.form.dynamic_venue_mode === 'none') return
      const sectionId = String(section.id || '').trim()
      if (!sectionId) return
      const selected = Array.isArray(this.form.dynamic_venue_section_ids) ? this.form.dynamic_venue_section_ids.map((id) => String(id)) : []
      if (selected.includes(sectionId)) {
        this.form.dynamic_venue_section_ids = selected.filter((id) => id !== sectionId)
        return
      }
      this.form.dynamic_venue_section_ids = selected.concat(sectionId)
    },

    activateDynamicVenueSelection() {
      if (this.form.dynamic_venue_mode !== 'none') return
      const firstSection = this.dynamicVenueSections[0]
      this.form.dynamic_venue_mode = firstSection ? this.dynamicVenueSectionMode(firstSection) : 'zone'
      this.form.dynamic_venue_section_ids = []
    },

    selectDynamicVenueSectionFromMap(section) {
      if (!section) return
      const nextMode = this.dynamicVenueSectionMode(section)
      if (this.form.dynamic_venue_mode !== nextMode) {
        this.form.dynamic_venue_mode = nextMode
        this.form.dynamic_venue_section_ids = []
      }
      this.toggleDynamicVenueSection(section)
    },

    clearDynamicVenueSections() {
      this.form.dynamic_venue_section_ids = []
    },

    inferDynamicVenueBinding(ticket) {
      if (!this.dynamicVenueEnabled || !this.dynamicVenueSections.length) {
        return { mode: 'none', sectionIds: [] }
      }

      const haystack = this.normalizeDynamicVenueKey(`${ticket.name || ''} ${ticket.description || ''}`)
      const matches = this.dynamicVenueSections.filter((section) => {
        const aliases = Array.isArray(section.aliases) ? section.aliases : []
        const values = [section.id, section.name, ...aliases]
        return values.some((value) => {
          const needle = this.normalizeDynamicVenueKey(value)
          return Boolean(needle && haystack.includes(needle))
        })
      })

      if (!matches.length) {
        return { mode: 'none', sectionIds: [] }
      }

      const hasSeatSection = matches.some((section) => this.dynamicVenueSectionMode(section) === 'seat')
      return {
        mode: hasSeatSection ? 'seat' : 'zone',
        sectionIds: matches.map((section) => String(section.id || '').trim()).filter(Boolean),
      }
    },

    setDynamicVenueMode(mode) {
      if (this.form.dynamic_venue_mode === mode) return
      this.form.dynamic_venue_mode = mode
      this.form.dynamic_venue_section_ids = []
    },

    isSelectedStageCode(codeId) {
      return Array.isArray(this.form.access_code_ids)
        && this.form.access_code_ids.some((selectedId) => Number(selectedId) === Number(codeId))
    },

    stageHasOptionalContent(ticket = {}, guestFields = []) {
      return (Number(ticket.quantity) || 1) > 1
        || (Number(ticket.type) || 1) !== 1
        || (Number(ticket.category) || 5) !== 5
        || Boolean(ticket.sales_date)
        || Boolean(ticket.expiration_date)
        || Boolean(ticket.max_code_uses)
        || Boolean(ticket.generic_image)
        || (Array.isArray(guestFields) && guestFields.length > 0)
    },

    fetchAll() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loading = true
      this.fetchVenueLayouts()
      this.fetchDynamicVenueLayout()
      Promise.all([
        apiService(`/api/host/events/${this.event.id}/tickets/`),
        apiService(`/api/host/events/${this.event.id}/codes/`),
        apiService(`/api/host/events/${this.event.id}/guest-fields/`),
      ]).then(([tickets, codes, fields]) => {
        this.tickets = Array.isArray(tickets) ? tickets : []
        this.codes = Array.isArray(codes) ? codes : []
        this.catalogFields = Array.isArray(fields) ? fields : []

        this.stageAssignments = this.pruneAssignments(this.stageAssignments)
        this.globalAssignments = this.pruneAssignments(this.globalAssignments)

        if (!this.globalAssignments.length) {
          const sourceTicket = this.stageTickets.find((ticket) => Array.isArray(ticket.guest_fields) && ticket.guest_fields.length)
          if (sourceTicket) {
            this.globalAssignments = this.mapGuestFieldsToAssignments(sourceTicket.guest_fields)
          }
        }
      }).finally(() => {
        this.loading = false
      })
    },

    fetchVenueLayouts() {
      if (this.loadingVenueLayouts) return Promise.resolve(this.venueLayouts)
      this.loadingVenueLayouts = true
      return apiService('/api/host/venue-layouts/', undefined, undefined, { timeoutMs: 20000 }).then((data) => {
        if (!Array.isArray(data)) {
          this.venueLayouts = []
          this.dynamicVenueControlError = this.extractDynamicVenueError(data, 'No pudimos cargar venues publicados.')
          return this.venueLayouts
        }

        this.venueLayouts = data
        this.syncSelectedDynamicVenueLayout()
        return this.venueLayouts
      }).finally(() => {
        this.loadingVenueLayouts = false
      })
    },

    hydrateDynamicVenueStateFromEvent() {
      const source = this.event || {}
      this.dynamicVenueState = {
        enabled: Boolean(source.dynamic_venue_enabled),
        status: source.dynamic_venue_status || 'off',
        layoutVersionId: source.dynamic_venue_layout_version_id || (source.dynamic_venue_layout_version && source.dynamic_venue_layout_version.id) || '',
        layoutId: '',
      }
      this.dynamicVenueLayout = null
      this.dynamicVenueProtection = { has_obligations: false, sections: {}, seats: {} }
      this.syncSelectedDynamicVenueLayout()
    },

    syncSelectedDynamicVenueLayout() {
      const currentLayout = this.currentDynamicVenueLayout
      if (currentLayout) {
        this.dynamicVenueSelectedLayoutId = currentLayout.id
        return
      }

      const selectedId = String(this.dynamicVenueSelectedLayoutId || '')
      const selectedStillExists = this.publishedVenueLayouts.some((layout) => String(layout.id) === selectedId)
      if (!selectedStillExists) {
        this.dynamicVenueSelectedLayoutId = ''
      }
    },

    applyDynamicVenuePayload(data, options = {}) {
      if (!data || data.err) return false

      this.dynamicVenueState = {
        enabled: Boolean(data.dynamic_venue_enabled),
        status: data.dynamic_venue_status || 'off',
        layoutVersionId: data.layout_version_id || '',
        layoutId: data.layout && data.layout.id ? data.layout.id : '',
      }

      const isActive = this.dynamicVenueState.enabled
        && this.dynamicVenueState.status === 'published'
        && this.dynamicVenueState.layoutVersionId
      this.dynamicVenueLayout = isActive && data.schema_json ? data.schema_json : null
      this.dynamicVenueProtection = data.protection || { has_obligations: false, sections: {}, seats: {} }
      this.syncSelectedDynamicVenueLayout()

      if (options.emitEvent && data.event) {
        this.$emit('event-updated', data.event)
      }

      return true
    },

    fetchDynamicVenueLayout() {
      if (!this.event || !this.event.id) {
        this.hydrateDynamicVenueStateFromEvent()
        return Promise.resolve(null)
      }

      this.loadingDynamicVenue = true
      return apiService(`/api/host/events/${this.event.id}/dynamic-venue/`, undefined, undefined, { timeoutMs: 20000 }).then((data) => {
        if (!data || data.err) {
          this.hydrateDynamicVenueStateFromEvent()
          return null
        }

        this.applyDynamicVenuePayload(data)
        return data
      }).finally(() => {
        this.loadingDynamicVenue = false
      })
    },

    assignDynamicVenueLayout(options = {}) {
      const layout = this.selectedDynamicVenueLayout
      if (!layout || !layout.published_version || !layout.published_version.id) {
        this.dynamicVenueControlError = 'Selecciona un venue publicado.'
        return Promise.resolve(null)
      }

      this.resetMessages()
      this.dynamicVenueControlError = ''
      this.dynamicVenueSaving = true

      return apiService(`/api/host/events/${this.event.id}/dynamic-venue/assign-layout/`, 'POST', {
        layout_version_id: layout.published_version.id,
      }, { timeoutMs: 20000 }).then((data) => {
        if (!data || data.err || data.valid === false) {
          this.dynamicVenueControlError = this.extractDynamicVenueError(data, 'No pudimos asignar el venue.')
          return null
        }

        this.applyDynamicVenuePayload(data, { emitEvent: true })
        this.successMessage = 'Mapa visual activo. Conecta las etapas desde el editor.'
        if (options.continueStageCreation) {
          this.activateDynamicVenueSelection()
          this.stageCreationPath = 'venue'
        }
        if (options.closeVenueManager) {
          this.successMessage = `Ahora ${layout.name} es el mapa activo del checkout.`
          this.resetForm()
        }
        return data
      }).finally(() => {
        this.dynamicVenueSaving = false
      })
    },

    unassignDynamicVenueLayout() {
      if (!this.event || !this.event.id) return

      this.resetMessages()
      this.dynamicVenueControlError = ''
      this.dynamicVenueSaving = true

      apiService(`/api/host/events/${this.event.id}/dynamic-venue/unassign-layout/`, 'POST', {}, { timeoutMs: 20000 }).then((data) => {
        if (!data || data.err) {
          this.dynamicVenueControlError = this.extractDynamicVenueError(data, 'No pudimos apagar el mapa visual.')
          return
        }

        this.applyDynamicVenuePayload(data, { emitEvent: true })
        this.form.dynamic_venue_mode = 'none'
        this.form.dynamic_venue_section_ids = []
        this.successMessage = 'Mapa visual apagado.'
      }).finally(() => {
        this.dynamicVenueSaving = false
      })
    },

    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    extractDynamicVenueError(data, fallback = 'No pudimos actualizar el mapa visual.') {
      if (!data) return fallback
      if (data.err) return data.err
      if (Array.isArray(data.errors) && data.errors.length) {
        const firstError = data.errors[0]
        return firstError && firstError.message ? firstError.message : String(firstError || fallback)
      }
      const firstValue = typeof data === 'object' ? Object.values(data)[0] : null
      if (Array.isArray(firstValue)) return firstValue[0] || fallback
      return firstValue || fallback
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)
    },

    stageTicketPricing(ticket) {
      return ticketUnitPricing(ticket)
    },

    displayTaxPercent(value) {
      const percent = normalizeTicketTaxPercent(value) * 100
      return Number.isFinite(percent) ? Number(percent.toFixed(4)) : 0
    },

    formatInputDate(value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return ''
      }

      const pad = (number) => `${number}`.padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    fieldTypeLabel(value) {
      return value === 'multiple' ? 'Seleccion unica' : 'Campo libre'
    },

    normalizeOptionsDraft(options) {
      return String(options || '')
        .split(/\r?\n/)
        .map((option) => option.trim())
        .filter((option, index, list) => option !== '' && list.indexOf(option) === index)
    },

    resetCatalogFieldEditor() {
      this.editingCatalogFieldId = null
      this.catalogForm = defaultCatalogForm()
      this.catalogOptionsDraft = ''
    },

    openCatalogFieldEditor(context = 'global', field = null) {
      this.catalogEditorContext = context
      this.catalogEditorOpen = true

      if (context === 'global') {
        this.catalogWorkspaceOpen = true
      }

      if (context === 'stage') {
        this.editorOpen = true
        this.advancedOpen = true
        this.stageGuestFieldsOpen = true
      }

      if (!field) {
        this.resetCatalogFieldEditor()
        return
      }

      this.editingCatalogFieldId = field.id
      this.catalogForm = {
        label: field.label || '',
        field_type: field.field_type || 'field',
        help_text: field.help_text || '',
        is_active: field.is_active !== false,
      }
      this.catalogOptionsDraft = Array.isArray(field.options) ? field.options.join('\n') : ''
    },

    closeCatalogFieldEditor() {
      this.catalogEditorOpen = false
      this.resetCatalogFieldEditor()
    },

    submitCatalogField() {
      this.resetMessages()
      this.catalogSaving = true

      const endpoint = this.editingCatalogFieldId
        ? `/api/host/events/${this.event.id}/guest-fields/${this.editingCatalogFieldId}/`
        : `/api/host/events/${this.event.id}/guest-fields/`

      const payload = {
        ...this.catalogForm,
        options: this.catalogForm.field_type === 'multiple' ? this.normalizeOptionsDraft(this.catalogOptionsDraft) : [],
      }

      apiService(endpoint, this.editingCatalogFieldId ? 'PATCH' : 'POST', payload).then((data) => {
        if (!data || data.err) {
          this.errorMessage = (data && data.err) || 'No se pudo guardar el campo reutilizable.'
          return
        }

        const createdFieldId = data.id
        this.successMessage = this.editingCatalogFieldId ? 'Campo reutilizable actualizado.' : 'Campo reutilizable creado.'

        if (!this.editingCatalogFieldId && this.catalogEditorContext === 'stage' && createdFieldId) {
          this.stageAssignments.push({ field_id: createdFieldId, is_required: false, sort_order: this.stageAssignments.length })
          this.reindexAssignments(this.stageAssignments)
        }

        if (!this.editingCatalogFieldId && this.catalogEditorContext === 'global' && createdFieldId) {
          this.globalAssignments.push({ field_id: createdFieldId, is_required: false, sort_order: this.globalAssignments.length })
          this.reindexAssignments(this.globalAssignments)
        }

        this.closeCatalogFieldEditor()
        this.fetchAll()
      }).finally(() => {
        this.catalogSaving = false
      })
    },

    deleteCatalogField(field) {
      if (!field || !window.confirm(`Eliminar ${field.label}? Se quitara del catalogo y de las etapas donde este asignado.`)) {
        return
      }

      this.resetMessages()
      apiService(`/api/host/events/${this.event.id}/guest-fields/${field.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        this.stageAssignments = this.pruneAssignments(this.stageAssignments.filter((assignment) => assignment.field_id !== field.id))
        this.globalAssignments = this.pruneAssignments(this.globalAssignments.filter((assignment) => assignment.field_id !== field.id))
        this.successMessage = 'Campo reutilizable eliminado.'
        if (this.editingCatalogFieldId === field.id) {
          this.closeCatalogFieldEditor()
        }
        this.fetchAll()
      }).catch(() => {
        this.errorMessage = 'No se pudo eliminar el campo reutilizable.'
      })
    },

    enrichAssignments(assignments) {
      return this.pruneAssignments(assignments)
        .map((assignment) => {
          const field = this.catalogFields.find((item) => item.id === assignment.field_id)
          if (!field) {
            return null
          }
          return {
            ...field,
            field_id: field.id,
            is_required: Boolean(assignment.is_required),
            sort_order: assignment.sort_order,
          }
        })
        .filter(Boolean)
        .sort((left, right) => left.sort_order - right.sort_order)
    },

    pruneAssignments(assignments) {
      const validIds = new Set(this.catalogFields.map((field) => field.id))
      const seen = new Set()
      const nextAssignments = []

      ;(Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
        const fieldId = Number(assignment.field_id)
        if (!validIds.has(fieldId) || seen.has(fieldId)) {
          return
        }
        seen.add(fieldId)
        nextAssignments.push({
          field_id: fieldId,
          is_required: Boolean(assignment.is_required),
          sort_order: nextAssignments.length,
        })
      })

      return nextAssignments
    },

    reindexAssignments(assignments) {
      assignments.forEach((assignment, index) => {
        assignment.sort_order = index
      })
    },

    isFieldSelected(assignments, fieldId) {
      return assignments.some((assignment) => Number(assignment.field_id) === Number(fieldId))
    },

    toggleAssignment(listName, fieldId) {
      const target = this[listName]
      const index = target.findIndex((assignment) => Number(assignment.field_id) === Number(fieldId))
      if (index >= 0) {
        target.splice(index, 1)
        this.reindexAssignments(target)
        return
      }
      target.push({ field_id: Number(fieldId), is_required: false, sort_order: target.length })
      this.reindexAssignments(target)
    },

    toggleStageAssignment(fieldId) {
      this.toggleAssignment('stageAssignments', fieldId)
    },

    toggleGlobalAssignment(fieldId) {
      this.toggleAssignment('globalAssignments', fieldId)
    },

    toggleAssignmentRequired(assignments, fieldId) {
      const target = assignments.find((assignment) => Number(assignment.field_id) === Number(fieldId))
      if (target) {
        target.is_required = !target.is_required
      }
    },

    moveAssignment(assignments, index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= assignments.length) {
        return
      }
      const current = assignments[index]
      assignments.splice(index, 1)
      assignments.splice(targetIndex, 0, current)
      this.reindexAssignments(assignments)
    },

    mapGuestFieldsToAssignments(fields) {
      return (Array.isArray(fields) ? fields : []).map((field, index) => ({
        field_id: Number(field.field_id || field.id),
        is_required: Boolean(field.is_required),
        sort_order: Number(field.sort_order == null ? index : field.sort_order),
      }))
    },

    buildAssignmentPayload(assignments) {
      return {
        assignments: this.pruneAssignments(assignments).map((assignment, index) => ({
          field_id: assignment.field_id,
          is_required: Boolean(assignment.is_required),
          sort_order: index,
        })),
      }
    },

    saveTicketGuestFields(ticketId, assignments) {
      return apiService(`/api/host/events/tickets/${ticketId}/guest-fields/`, 'PUT', this.buildAssignmentPayload(assignments))
    },

    toggleCatalogWorkspace() {
      this.catalogWorkspaceOpen = !this.catalogWorkspaceOpen
      if (this.catalogWorkspaceOpen && !this.globalAssignments.length) {
        const sourceTicket = this.stageTickets.find((ticket) => Array.isArray(ticket.guest_fields) && ticket.guest_fields.length)
        if (sourceTicket) {
          this.globalAssignments = this.mapGuestFieldsToAssignments(sourceTicket.guest_fields)
        }
      }
    },

    applyGlobalAssignmentsToCurrentStages() {
      const confirmationText = this.globalAssignments.length
        ? `Aplicar esta configuracion a las ${this.stageTickets.length} etapas actuales?`
        : `Quitar los campos extra de las ${this.stageTickets.length} etapas actuales y dejar solo Nombre y Apellido?`

      if (!window.confirm(confirmationText)) {
        return
      }

      this.resetMessages()
      this.applyingGlobalAssignments = true

      Promise.all(this.stageTickets.map((ticket) => this.saveTicketGuestFields(ticket.id, this.globalAssignments)))
        .then((responses) => {
          const failed = responses.find((response) => response && response.err)
          if (failed) {
            this.errorMessage = failed.err || 'No se pudo aplicar la configuracion global.'
            return
          }
          this.successMessage = 'Configuracion aplicada a todas las etapas actuales.'
          this.fetchAll()
        })
        .finally(() => {
          this.applyingGlobalAssignments = false
        })
    },

    openCreatePanel(options = {}) {
      this.resetMessages()
      this.editingTicketId = null
      this.editorOpen = true
      this.stageCreationPath = ''
      this.venueSelectionPurpose = 'stage'
      this.firstStageVenuePreview = null
      this.firstStageVenuePreviewLoading = false
      this.firstStageVenuePreviewError = ''
      this.advancedOpen = false
      this.stageGuestFieldsOpen = Boolean(options.openGuestFields)
      this.stageCodeSearch = ''
      this.stageImageFile = null
      this.stageImagePreview = ''
      this.codeLimitEnabled = false
      this.form = defaultForm()
      this.stageAssignments = []
      this.closeCatalogFieldEditor()
    },

    selectFirstStageMode(mode) {
      if (mode === 'list') {
        this.setDynamicVenueMode('none')
        this.stageCreationPath = 'list'
        return
      }

      if (mode !== 'venue' || this.firstStageVenueLoading) return
      this.venueSelectionPurpose = 'stage'
      this.stageCreationPath = 'venue-select'
      this.dynamicVenueControlError = ''
      if (!this.publishedVenueLayouts.length) return

      const initialLayout = this.currentDynamicVenueLayout || this.selectedDynamicVenueLayout || this.publishedVenueLayouts[0]
      this.selectFirstStageVenueLayout(initialLayout)
    },

    openVenueChangePanel() {
      if (!this.dynamicVenueEnabled || this.dynamicVenueSaving) return
      this.resetMessages()
      this.editingTicketId = null
      this.editorOpen = true
      this.stageCreationPath = 'venue-select'
      this.venueSelectionPurpose = 'manage'
      this.dynamicVenueControlError = ''
      this.firstStageVenuePreview = null
      this.firstStageVenuePreviewLoading = false
      this.firstStageVenuePreviewError = ''
      const initialLayout = this.currentDynamicVenueLayout || this.selectedDynamicVenueLayout || this.publishedVenueLayouts[0]
      if (initialLayout) this.selectFirstStageVenueLayout(initialLayout)
    },

    selectFirstStageVenueLayout(layout) {
      if (!layout || !layout.id) return
      const layoutId = String(layout.id)
      this.dynamicVenueSelectedLayoutId = layout.id
      this.firstStageVenuePreview = null
      this.firstStageVenuePreviewError = ''
      this.firstStageVenuePreviewLoading = true

      apiService(`/api/host/venue-layouts/${layout.id}/`, undefined, undefined, { timeoutMs: 20000 }).then((data) => {
        if (String(this.dynamicVenueSelectedLayoutId) !== layoutId) return
        if (!data || data.err) {
          this.firstStageVenuePreviewError = this.extractDynamicVenueError(data, 'No pudimos cargar el venue publicado.')
          return
        }
        this.firstStageVenuePreview = data
      }).finally(() => {
        if (String(this.dynamicVenueSelectedLayoutId) === layoutId) this.firstStageVenuePreviewLoading = false
      })
    },

    reloadFirstStageVenuePreview() {
      if (this.selectedDynamicVenueLayout) this.selectFirstStageVenueLayout(this.selectedDynamicVenueLayout)
    },

    returnToFirstStageModes() {
      if (this.venueSelectionPurpose === 'manage') {
        this.resetForm()
        this.syncSelectedDynamicVenueLayout()
        this.dynamicVenueControlError = ''
        return
      }
      this.stageCreationPath = ''
      this.firstStageVenuePreview = null
      this.firstStageVenuePreviewLoading = false
      this.firstStageVenuePreviewError = ''
      this.dynamicVenueControlError = ''
    },

    confirmFirstStageVenue() {
      if (!this.canContinueWithFirstStageVenue) return
      if (this.dynamicVenueSelectedIsAssigned && this.dynamicVenueEditorAvailable) {
        if (this.venueSelectionPurpose === 'manage') {
          this.resetForm()
          this.successMessage = 'Se mantiene el mapa activo del checkout.'
          return
        }
        this.activateDynamicVenueSelection()
        this.stageCreationPath = 'venue'
        return
      }
      this.assignDynamicVenueLayout({
        continueStageCreation: this.venueSelectionPurpose !== 'manage',
        closeVenueManager: this.venueSelectionPurpose === 'manage',
      })
    },

    openVenueStudioFromFirstStage() {
      this.resetForm()
      this.$router.push({ name: 'tools2', query: { createVenue: '1' } })
    },

    stageVisibilityLabel(ticket) {
      return ticket.web_experience ? 'Visible' : 'Oculta'
    },

    stageCodeAccessLabel(ticket) {
      if (!ticket.web_experience && Number(ticket.code_mode) === 0) {
        return 'Link privado'
      }

      if (Number(ticket.code_mode) === 1) {
        return 'Cualquier código'
      }

      if (Number(ticket.code_mode) === 2) {
        if (ticket.code && (!Array.isArray(ticket.access_codes_detail) || !ticket.access_codes_detail.length)) {
          return 'Link privado'
        }
        const code = this.stagePrimaryCode(ticket)
        if (Array.isArray(ticket.access_codes_detail) && ticket.access_codes_detail.length > 1) {
          return `${ticket.access_codes_detail.length} códigos elegidos`
        }
        if (code) {
          return `Link privado · ${code}`
        }
        return 'Códigos elegidos'
      }

      return 'Sin código'
    },

    dynamicVenueStageLabel(ticket) {
      if (!this.dynamicVenueEnabled) return ''
      const mode = ticket.dynamic_venue_mode || 'none'
      const sectionIds = Array.isArray(ticket.dynamic_venue_section_ids) ? ticket.dynamic_venue_section_ids : []
      if (mode === 'none' || !sectionIds.length) return ''

      const sections = sectionIds
        .map((sectionId) => this.dynamicVenueSections.find((section) => String(section.id) === String(sectionId)))
        .filter(Boolean)
      const names = sections.map((section) => section.name).filter(Boolean)
      const prefix = mode === 'seat' ? 'Mapa: asiento/mesa' : 'Mapa: zona'
      if (!names.length) return `${prefix} asignado`
      if (names.length > 2) return `${prefix} · ${names.slice(0, 2).join(', ')} +${names.length - 2}`
      return `${prefix} · ${names.join(', ')}`
    },

    comboLabel(ticket) {
      const quantity = Number(ticket.quantity) || 1
      return quantity > 1 ? `${quantity}x por unidad` : 'Simple'
    },

    accessCodeSummary(ticket) {
      const count = Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.length : 0
      if (!count) {
        return 'Sin seleccion'
      }
      if (count === 1) {
        return ticket.access_codes_detail[0].code
      }
      return `${count} códigos`
    },

    codeLimitLabel(ticket) {
      if (ticket.web_experience || Number(ticket.code_mode) === 0) {
        return 'No aplica'
      }
      return ticket.max_code_uses ? `${ticket.max_code_uses} usos max.` : 'Sin limite'
    },

    guestFieldsCountLabel(ticket) {
      const count = Array.isArray(ticket.guest_fields) ? ticket.guest_fields.length : 0
      return count ? `${count} configurados` : 'Sin campos'
    },

    normalizedPayload() {
      const selectedCode = this.codes.find((code) => code.id === Number(this.form.access_code_ids[0]))
      const codeAvailability = this.stageCodeAvailability
      const shouldAutoGeneratePrivateCode = this.stageCheckoutVisibility === 'hidden' && codeAvailability === 'none'
      const privateCode = shouldAutoGeneratePrivateCode ? this.generateStagePrivateCode() : ''
      return {
        ...this.form,
        code_mode: codeAvailability === 'all' ? 1 : codeAvailability === 'selected' || shouldAutoGeneratePrivateCode ? 2 : 0,
        is_courtesy: this.isCourtesyPrice,
        category: Number(this.form.category),
        initial_quantity: Number(this.form.initial_quantity),
        price: Number(this.form.price),
        tax_enabled: Boolean(this.form.tax_enabled),
        tax_label: this.form.tax_label || 'IVA',
        tax_percent: this.form.tax_enabled ? normalizeTicketTaxPercent(this.form.tax_percent) : 0,
        tax_price_includes_tax: Boolean(this.form.tax_enabled && this.form.tax_price_includes_tax),
        quantity: Number(this.form.quantity),
        max_quantity: Number(this.form.max_quantity),
        max_code_uses: this.codeUsageLimitApplies && this.codeLimitEnabled && this.form.max_code_uses ? Number(this.form.max_code_uses) : null,
        web_experience: this.stageCheckoutVisibility === 'checkout',
        require_ig: false,
        sales_date: this.form.sales_date ? `${this.form.sales_date}:00` : null,
        expiration_date: this.form.expiration_date ? `${this.form.expiration_date}:00` : null,
        access_code_ids: codeAvailability === 'selected' ? this.form.access_code_ids : [],
        code: shouldAutoGeneratePrivateCode ? privateCode : codeAvailability === 'selected' && selectedCode ? selectedCode.code : '',
        type: this.isCourtesyPrice ? 3 : 1,
        dynamic_venue_mode: this.dynamicVenueEnabled ? this.form.dynamic_venue_mode : 'none',
        dynamic_venue_section_ids: this.dynamicVenueEnabled && this.form.dynamic_venue_mode !== 'none' ? this.form.dynamic_venue_section_ids : [],
      }
    },

    submitStage() {
      if (this.dynamicVenueEnabled && this.form.dynamic_venue_mode !== 'none' && !this.form.dynamic_venue_section_ids.length) {
        this.resetMessages()
        this.errorMessage = 'Falta sección del venue.'
        return
      }

      if (this.stageCodeAvailability === 'selected' && !this.form.access_code_ids.length) {
        this.resetMessages()
        this.errorMessage = 'Selecciona al menos un código para dejar la etapa ligada a códigos elegidos.'
        return
      }

      this.resetMessages()
      this.saving = true

      const endpoint = this.editingTicketId
        ? `/api/host/events/tickets/${this.editingTicketId}/`
        : `/api/host/events/${this.event.id}/tickets/`

      apiService(endpoint, this.editingTicketId ? 'PATCH' : 'POST', this.normalizedPayload())
        .then((data) => {
          if (!data || data.err || !data.id) {
            this.errorMessage = this.extractDynamicVenueError(data, 'No se pudo guardar la etapa.')
            return null
          }
          return data
        })
        .then((ticket) => {
          if (!ticket || !ticket.id) {
            return null
          }
          return this.uploadStageImage(ticket.id)
            .then((imageResponse) => {
              if (imageResponse && imageResponse.ok === false) {
                this.errorMessage = imageResponse.err
              }

              return this.saveTicketGuestFields(ticket.id, this.stageAssignments)
            })
            .then((assignmentResponse) => {
              if (assignmentResponse && assignmentResponse.err) {
                this.errorMessage = assignmentResponse.err || 'La etapa se guardo, pero no pudimos actualizar sus campos extra.'
                return null
              }

              if (this.isCourtesyPrice) {
                this.successMessage = this.editingTicketId ? 'Guardada como cortesia y actualizada.' : 'Guardada como cortesia.'
              } else {
                this.successMessage = this.editingTicketId ? 'Etapa y campos extra actualizados.' : 'Etapa y campos extra creados.'
              }
              this.fetchAll()
              this.resetForm()
              return assignmentResponse
            })
        })
        .finally(() => {
          this.saving = false
        })
    },

    editStage(ticket, options = {}) {
      this.resetMessages()
      this.editingTicketId = ticket.id
      this.editorOpen = true
      this.stageCreationPath = ''
      this.codeLimitEnabled = Boolean(ticket.max_code_uses)
      this.stageAssignments = this.mapGuestFieldsToAssignments(ticket.guest_fields)
      this.stageCodeSearch = ''
      this.stageImageFile = null
      this.stageImagePreview = ticket.generic_image || ''
      this.advancedOpen = Boolean(options.openGuestFields || this.stageHasOptionalContent(ticket, this.stageAssignments))
      this.stageGuestFieldsOpen = Boolean(options.openGuestFields || this.stageAssignments.length)
      this.closeCatalogFieldEditor()
      const persistedDynamicVenueMode = ['zone', 'seat'].includes(String(ticket.dynamic_venue_mode || 'none')) ? ticket.dynamic_venue_mode : 'none'
      const persistedDynamicVenueSections = Array.isArray(ticket.dynamic_venue_section_ids)
        ? ticket.dynamic_venue_section_ids.map((sectionId) => String(sectionId || '').trim()).filter(Boolean)
        : []
      const inferredDynamicVenueBinding = persistedDynamicVenueMode !== 'none' && persistedDynamicVenueSections.length
        ? { mode: persistedDynamicVenueMode, sectionIds: persistedDynamicVenueSections }
        : this.inferDynamicVenueBinding(ticket)
      this.form = {
        name: ticket.name || '',
        description: ticket.description || '',
        initial_quantity: ticket.initial_quantity || 0,
        price: Number.parseFloat(ticket.price) || 0,
        tax_enabled: Boolean(ticket.tax_enabled),
        tax_label: ticket.tax_label || 'IVA',
        tax_percent: this.displayTaxPercent(ticket.tax_percent),
        tax_price_includes_tax: Boolean(ticket.tax_price_includes_tax),
        status: Boolean(ticket.status),
        web_experience: Boolean(ticket.web_experience),
        is_courtesy: Boolean(ticket.is_courtesy),
        type: Number(ticket.type) || 1,
        category: Number(ticket.category) || 5,
        sales_date: this.formatInputDate(ticket.sales_date),
        expiration_date: this.formatInputDate(ticket.expiration_date),
        require_ig: Boolean(ticket.require_ig),
        quantity: Number(ticket.quantity) || 1,
        max_quantity: Number(ticket.max_quantity) || 5,
        max_code_uses: Number(ticket.max_code_uses) || null,
        code: ticket.code || '',
        code_mode: Number(ticket.code_mode) || 0,
        access_code_ids: Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.map((code) => code.id) : [],
        dynamic_venue_mode: inferredDynamicVenueBinding.mode,
        dynamic_venue_section_ids: inferredDynamicVenueBinding.sectionIds,
      }
    },

    resetForm() {
      this.editingTicketId = null
      this.editorOpen = false
      this.stageCreationPath = ''
      this.venueSelectionPurpose = ''
      this.firstStageVenuePreview = null
      this.firstStageVenuePreviewLoading = false
      this.firstStageVenuePreviewError = ''
      this.advancedOpen = false
      this.stageGuestFieldsOpen = false
      this.stageCodeSearch = ''
      this.stageImageFile = null
      this.stageImagePreview = ''
      if (this.$refs.stageImageInput) {
        this.$refs.stageImageInput.value = ''
      }
      this.codeLimitEnabled = false
      this.form = defaultForm()
      this.stageAssignments = []
      this.closeCatalogFieldEditor()
    },

    deleteStage(ticket) {
      this.resetMessages()
      apiService(`/api/host/events/tickets/${ticket.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }
        this.successMessage = ticket.status ? 'Etapa archivada.' : 'Etapa eliminada.'
        if (this.editingTicketId === ticket.id) {
          this.resetForm()
        }
        this.fetchAll()
      }).catch(() => {
        this.errorMessage = 'No se pudo eliminar la etapa.'
      })
    },
  },
}
</script>

<style scoped>
.workspace-panel--hero,
.workspace-editor-panel,
.workspace-subpanel,
.workspace-list-stack,
.workspace-preview-stack,
.workspace-assignment-card,
.workspace-card--soft {
  display: grid;
  gap: 18px;
}

.workspace-panel__header--spread,
.workspace-subpanel__head,
.workspace-assignment-card,
.workspace-limit-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-subpanel__head--tight,
.workspace-assignment-card--catalog {
  gap: 12px;
}

.stage-venue-control {
  gap: 16px;
}

.stage-venue-control--active {
  border-color: rgba(172, 124, 229, 0.22);
  background:
    radial-gradient(circle at 88% 15%, rgba(130, 72, 180, 0.14), transparent 30%),
    rgba(10, 8, 17, 0.88);
}

.stage-venue-control__active-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.stage-venue-control__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-venue-control__facts span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 7px 11px;
  border: 1px solid rgba(223, 214, 255, 0.12);
  border-radius: 999px;
  color: rgba(238, 232, 255, 0.8);
  font-size: 0.78rem;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.035);
}

.stage-venue-control__facts .is-protected {
  border-color: rgba(181, 138, 216, 0.28);
  color: #ead7ff;
  background: rgba(95, 52, 125, 0.22);
}

.stage-venue-control__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.stage-venue-control__error {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid rgba(255, 133, 149, 0.22);
  border-radius: 14px;
  color: #ffe4e8;
  font-weight: 800;
  background: rgba(235, 68, 88, 0.12);
}

.stage-entry-picker {
  display: grid;
  gap: 24px;
  padding: 4px 2px 10px;
}

.stage-entry-picker__intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 0 4px;
}

.stage-entry-picker__intro p,
.stage-entry-picker__note {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.55;
}

.stage-entry-picker__intro p {
  max-width: 670px;
}

.stage-entry-picker__step {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(156, 105, 196, 0.28);
  border-radius: 999px;
  color: #dcc6ec;
  background: rgba(83, 43, 113, 0.18);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.stage-entry-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.stage-entry-card {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-rows: auto auto minmax(210px, 1fr) auto;
  gap: 20px;
  min-width: 0;
  min-height: 520px;
  padding: 24px;
  overflow: hidden;
  border: 1px solid rgba(211, 220, 240, 0.13);
  border-radius: 30px;
  color: var(--text-main);
  text-align: left;
  background: linear-gradient(155deg, rgba(20, 28, 42, 0.96), rgba(7, 11, 18, 0.98) 62%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 22px 54px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease;
  animation: stage-entry-card-arrive 480ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.stage-entry-card--venue {
  animation-delay: 90ms;
}

.stage-entry-card::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 0;
  opacity: 0.58;
  pointer-events: none;
}

.stage-entry-card--list::before {
  background:
    radial-gradient(circle at 14% 4%, rgba(116, 69, 151, 0.3), transparent 30%),
    linear-gradient(135deg, rgba(75, 39, 102, 0.14), transparent 48%);
}

.stage-entry-card--venue::before {
  background:
    radial-gradient(circle at 86% 5%, rgba(180, 145, 255, 0.24), transparent 31%),
    linear-gradient(225deg, rgba(151, 112, 236, 0.1), transparent 50%);
}

.stage-entry-card:hover:not(:disabled),
.stage-entry-card:focus-visible {
  transform: translateY(-6px);
  outline: none;
}

.stage-entry-card--list:hover:not(:disabled),
.stage-entry-card--list:focus-visible {
  border-color: rgba(170, 121, 207, 0.46);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.09), 0 26px 68px rgba(51, 24, 72, 0.34);
}

.stage-entry-card--venue:hover:not(:disabled),
.stage-entry-card--venue:focus-visible {
  border-color: rgba(188, 155, 255, 0.42);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.09), 0 26px 68px rgba(91, 61, 153, 0.22);
}

.stage-entry-card:disabled {
  cursor: wait;
  opacity: 0.78;
}

.stage-entry-card__glow {
  position: absolute;
  z-index: -1;
  width: 180px;
  height: 180px;
  right: -90px;
  bottom: -100px;
  border-radius: 999px;
  filter: blur(10px);
  opacity: 0.22;
  transition: opacity 220ms ease, transform 400ms ease;
}

.stage-entry-card--list .stage-entry-card__glow {
  background: #6f438f;
}

.stage-entry-card--venue .stage-entry-card__glow {
  background: #ae83ff;
}

.stage-entry-card:hover .stage-entry-card__glow,
.stage-entry-card:focus-visible .stage-entry-card__glow {
  opacity: 0.36;
  transform: scale(1.25);
}

.stage-entry-card__topline,
.stage-entry-card__action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.stage-entry-card__icon {
  display: inline-grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 15px;
  font-size: 1.08rem;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.stage-entry-card--list .stage-entry-card__icon {
  color: #d2b4e7;
}

.stage-entry-card--venue .stage-entry-card__icon {
  color: #c9adff;
}

.stage-entry-card__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 0 11px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  border-radius: 999px;
  color: rgba(223, 231, 245, 0.7);
  background: rgba(255, 255, 255, 0.035);
  font-size: 0.72rem;
  font-weight: 800;
}

.stage-entry-card__status > i {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #a879ca;
  box-shadow: 0 0 0 5px rgba(142, 93, 180, 0.14);
  animation: stage-entry-status-pulse 2.4s ease-in-out infinite;
}

.stage-entry-card--venue .stage-entry-card__status:not(.is-ready) > i {
  background: #b998ff;
  box-shadow: 0 0 0 5px rgba(185, 152, 255, 0.1);
}

.stage-entry-card__copy {
  display: grid;
  gap: 7px;
}

.stage-entry-card__copy small {
  color: rgba(206, 217, 235, 0.58);
  font-size: 0.71rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.stage-entry-card__copy strong {
  color: #f7fbff;
  font-size: clamp(1.35rem, 2.2vw, 1.85rem);
  line-height: 1.12;
  letter-spacing: -0.035em;
}

.stage-entry-card__copy > span {
  max-width: 460px;
  color: rgba(213, 222, 238, 0.72);
  line-height: 1.55;
}

.stage-entry-list-preview,
.stage-entry-map-preview {
  position: relative;
  display: block;
  min-width: 0;
  height: 230px;
  overflow: hidden;
  border: 1px solid rgba(196, 206, 228, 0.11);
  border-radius: 22px;
  background: rgba(2, 6, 12, 0.5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.stage-entry-list-preview {
  padding: 16px;
  background:
    radial-gradient(circle at 80% 0, rgba(119, 72, 154, 0.14), transparent 42%),
    rgba(2, 7, 13, 0.62);
}

.stage-entry-list-preview__toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(196, 206, 228, 0.08);
}

.stage-entry-list-preview__toolbar > i {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgba(236, 243, 255, 0.25);
}

.stage-entry-list-preview__toolbar > i:first-child {
  background: #9e70c0;
}

.stage-entry-list-preview__toolbar em {
  margin-left: auto;
  color: rgba(212, 226, 239, 0.45);
  font-size: 0.56rem;
  font-style: normal;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.stage-entry-list-preview__row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 54px 24px;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  margin-top: 7px;
  padding: 7px 8px;
  border: 1px solid rgba(196, 206, 228, 0.075);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.035);
  opacity: 0;
  animation: stage-entry-list-row 5.2s ease-in-out infinite;
  animation-delay: calc(var(--row-index) * 150ms);
}

.stage-entry-list-preview__number {
  color: rgba(204, 170, 229, 0.72);
  font-size: 0.62rem;
  font-weight: 900;
}

.stage-entry-list-preview__lines {
  display: grid;
  gap: 5px;
}

.stage-entry-list-preview__lines i,
.stage-entry-list-preview__price {
  display: block;
  height: 5px;
  border-radius: 999px;
  background: rgba(226, 235, 249, 0.17);
}

.stage-entry-list-preview__lines i:last-child {
  width: 58%;
  background: rgba(226, 235, 249, 0.09);
}

.stage-entry-list-preview__price {
  width: 48px;
  background: rgba(151, 103, 187, 0.24);
}

.stage-entry-list-preview__check {
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  color: #d7bce9;
  background: rgba(126, 79, 162, 0.2);
  font-size: 0.52rem;
}

.stage-entry-map-preview {
  background:
    radial-gradient(circle at 50% 42%, rgba(148, 111, 226, 0.11), transparent 48%),
    rgba(2, 5, 11, 0.68);
}

.stage-entry-map-preview svg {
  display: block;
  width: 100%;
  height: 100%;
}

.stage-entry-map-preview__floor {
  fill: url(#firstStageVenueFloor);
  stroke: rgba(212, 220, 240, 0.13);
  stroke-width: 2;
}

.stage-entry-map-preview__zones path {
  fill: rgba(159, 123, 232, 0.11);
  stroke: rgba(187, 154, 255, 0.48);
  stroke-width: 2;
  transform-box: fill-box;
  transform-origin: center;
  animation: stage-entry-map-zone 4.2s ease-in-out infinite;
}

.stage-entry-map-preview__zones path:nth-child(2) {
  animation-delay: 350ms;
}

.stage-entry-map-preview__zones path:nth-child(3) {
  animation-delay: 700ms;
}

.stage-entry-map-preview__zones path:nth-child(4) {
  animation-delay: 1050ms;
}

.stage-entry-map-preview__route {
  fill: none;
  stroke: url(#firstStageVenueRoute);
  stroke-width: 4;
  stroke-linecap: round;
  stroke-dasharray: 9 12;
  opacity: 0.8;
  animation: stage-entry-map-route 3.2s linear infinite;
}

.stage-entry-map-preview__seats circle {
  fill: rgba(238, 244, 255, 0.7);
  stroke: rgba(8, 12, 20, 0.8);
  stroke-width: 2;
}

.stage-entry-map-preview__pin circle:first-child {
  fill: rgba(139, 89, 177, 0.2);
  stroke: rgba(190, 148, 220, 0.72);
  animation: stage-entry-map-pin 2.2s ease-out infinite;
}

.stage-entry-map-preview__pin circle:last-child {
  fill: #c9a8df;
  filter: drop-shadow(0 0 7px rgba(180, 133, 213, 0.72));
}

.stage-entry-map-preview__legend {
  position: absolute;
  left: 14px;
  bottom: 13px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(202, 215, 237, 0.12);
  border-radius: 999px;
  color: rgba(224, 233, 247, 0.72);
  background: rgba(4, 8, 15, 0.82);
  backdrop-filter: blur(10px);
  font-size: 0.65rem;
  font-weight: 800;
}

.stage-entry-map-preview__legend i {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #b998ff;
  box-shadow: 0 0 10px rgba(185, 152, 255, 0.65);
}

.stage-entry-card__action {
  min-height: 48px;
  padding: 0 4px;
  border-top: 1px solid rgba(196, 206, 228, 0.09);
  color: #f5f8ff;
  font-weight: 900;
}

.stage-entry-card__action > i {
  transition: transform 180ms ease;
}

.stage-entry-card:hover:not(:disabled) .stage-entry-card__action > i,
.stage-entry-card:focus-visible .stage-entry-card__action > i {
  transform: translateX(5px);
}

.stage-entry-picker__note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  text-align: center;
  font-size: 0.82rem;
}

.stage-entry-picker__note i {
  color: #bba0f5;
}

@keyframes stage-entry-card-arrive {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes stage-entry-list-row {
  0%, 8% {
    opacity: 0;
    transform: translateX(-14px);
  }

  20%, 76% {
    opacity: 1;
    transform: translateX(0);
  }

  92%, 100% {
    opacity: 0;
    transform: translateX(10px);
  }
}

@keyframes stage-entry-map-route {
  to {
    stroke-dashoffset: -42;
  }
}

@keyframes stage-entry-map-zone {
  0%, 100% {
    fill: rgba(159, 123, 232, 0.1);
    stroke: rgba(187, 154, 255, 0.36);
    transform: scale(1);
  }

  45% {
    fill: rgba(122, 75, 156, 0.26);
    stroke: rgba(197, 154, 225, 0.74);
    transform: scale(1.018);
  }
}

@keyframes stage-entry-map-pin {
  0% {
    opacity: 1;
    transform: scale(0.5);
  }

  80%, 100% {
    opacity: 0;
    transform: scale(1.8);
  }
}

@keyframes stage-entry-status-pulse {
  0%, 100% {
    opacity: 0.65;
    transform: scale(0.82);
  }

  50% {
    opacity: 1;
    transform: scale(1);
  }
}

.stage-venue-onboarding {
  display: grid;
  gap: 18px;
  min-width: 0;
}

.stage-venue-onboarding__back {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  width: fit-content;
  padding: 6px 2px;
  border: 0;
  color: rgba(222, 211, 233, 0.7);
  background: transparent;
  font-weight: 800;
  transition: color 160ms ease, transform 160ms ease;
}

.stage-venue-onboarding__back:hover,
.stage-venue-onboarding__back:focus-visible {
  color: #fff;
  outline: none;
  transform: translateX(-3px);
}

.stage-venue-onboarding__shell {
  display: grid;
  grid-template-columns: minmax(260px, 0.72fr) minmax(0, 1.55fr);
  gap: 18px;
  min-width: 0;
}

.stage-venue-onboarding__selector,
.stage-venue-onboarding__preview,
.stage-venue-onboarding-empty {
  border: 1px solid rgba(190, 162, 211, 0.13);
  border-radius: 28px;
  background:
    linear-gradient(155deg, rgba(18, 14, 23, 0.98), rgba(6, 5, 9, 0.99)),
    #070609;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.045), 0 20px 48px rgba(0, 0, 0, 0.22);
}

.stage-venue-onboarding__selector {
  display: grid;
  align-content: start;
  gap: 18px;
  padding: 22px;
}

.stage-venue-onboarding__heading,
.stage-venue-onboarding__preview-head > div,
.stage-venue-onboarding-empty__copy {
  display: grid;
  gap: 7px;
}

.stage-venue-onboarding__heading h3,
.stage-venue-onboarding__preview-head h3,
.stage-venue-onboarding-empty__copy h3 {
  margin: 0;
  color: #f8f5fb;
  letter-spacing: -0.025em;
}

.stage-venue-onboarding__heading p,
.stage-venue-onboarding-empty__copy p {
  margin: 0;
  color: rgba(219, 211, 228, 0.65);
  line-height: 1.55;
}

.stage-venue-onboarding__options {
  display: grid;
  gap: 10px;
  max-height: 430px;
  padding-right: 3px;
  overflow-y: auto;
}

.stage-venue-option {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 26px;
  align-items: center;
  gap: 11px;
  min-width: 0;
  padding: 13px;
  border: 1px solid rgba(197, 176, 214, 0.1);
  border-radius: 17px;
  color: #f4eff8;
  text-align: left;
  background: rgba(255, 255, 255, 0.025);
  transition: border-color 170ms ease, background 170ms ease, transform 170ms ease;
}

.stage-venue-option:hover,
.stage-venue-option:focus-visible {
  border-color: rgba(156, 111, 190, 0.34);
  background: rgba(101, 59, 132, 0.1);
  outline: none;
  transform: translateY(-1px);
}

.stage-venue-option.is-active {
  border-color: rgba(174, 128, 207, 0.5);
  background: linear-gradient(135deg, rgba(91, 48, 121, 0.28), rgba(36, 23, 48, 0.34));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.055), 0 12px 28px rgba(27, 13, 38, 0.22);
}

.stage-venue-option__icon,
.stage-venue-option__check {
  display: inline-grid;
  place-items: center;
  border: 1px solid rgba(199, 179, 215, 0.12);
  background: rgba(255, 255, 255, 0.035);
}

.stage-venue-option__icon {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  color: #b68bd4;
}

.stage-venue-option__check {
  width: 25px;
  height: 25px;
  border-radius: 999px;
  color: transparent;
  font-size: 0.65rem;
}

.stage-venue-option.is-active .stage-venue-option__check {
  color: #f8f1fc;
  border-color: rgba(182, 139, 212, 0.42);
  background: rgba(116, 68, 150, 0.38);
}

.stage-venue-option__copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.stage-venue-option__copy strong,
.stage-venue-option__copy small,
.stage-venue-option__copy em {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-venue-option__copy strong {
  color: #faf7fc;
}

.stage-venue-option__copy small,
.stage-venue-option__copy em {
  color: rgba(216, 206, 225, 0.58);
  font-size: 0.72rem;
  font-style: normal;
}

.stage-venue-option__copy em {
  color: #b893d0;
  font-weight: 800;
}

.stage-venue-onboarding__preview {
  display: grid;
  grid-template-rows: auto minmax(300px, 1fr) auto auto;
  gap: 16px;
  min-width: 0;
  padding: 22px;
  overflow: hidden;
}

.stage-venue-onboarding__preview-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.stage-venue-onboarding__active-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 0 11px;
  border: 1px solid rgba(166, 124, 197, 0.26);
  border-radius: 999px;
  color: #d8bee9;
  background: rgba(92, 51, 121, 0.18);
  font-size: 0.72rem;
  font-weight: 900;
  white-space: nowrap;
}

.stage-venue-onboarding__active-badge i {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #a979c9;
  box-shadow: 0 0 0 5px rgba(132, 83, 167, 0.14);
}

.stage-venue-preview-loading,
.stage-venue-preview-error {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  min-height: 320px;
  padding: 24px;
  border: 1px solid rgba(197, 178, 212, 0.09);
  border-radius: 22px;
  color: rgba(224, 216, 231, 0.68);
  text-align: center;
  background: rgba(2, 2, 4, 0.45);
}

.stage-venue-preview-loading__map {
  width: min(74%, 360px);
  height: 150px;
  border-radius: 24px;
  background: linear-gradient(100deg, rgba(255, 255, 255, 0.025) 20%, rgba(119, 76, 150, 0.14) 42%, rgba(255, 255, 255, 0.025) 64%);
  background-size: 220% 100%;
  animation: stage-venue-preview-shimmer 1.8s linear infinite;
}

.stage-venue-preview-loading > span:nth-child(2) {
  display: flex;
  gap: 9px;
  width: min(64%, 300px);
}

.stage-venue-preview-loading > span:nth-child(2) i {
  display: block;
  width: 50%;
  height: 9px;
  border-radius: 999px;
  background: rgba(174, 146, 195, 0.1);
}

.stage-venue-preview-loading p {
  margin: 0;
  font-weight: 800;
}

.stage-venue-preview-error > i {
  color: #ad83c9;
  font-size: 1.35rem;
}

.stage-venue-preview-error strong {
  color: #f5f0f8;
}

.stage-venue-preview-error span {
  max-width: 480px;
  line-height: 1.5;
}

.stage-venue-preview-error button {
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(174, 132, 204, 0.28);
  border-radius: 11px;
  color: #efe6f5;
  background: rgba(91, 52, 119, 0.22);
  font-weight: 800;
}

.stage-venue-preview-error--empty {
  border-style: dashed;
}

.stage-venue-preview-map {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.stage-venue-preview-map :deep(.host-mini-map) {
  height: 100%;
  min-height: 320px;
  padding: 10px;
  border-color: rgba(187, 158, 209, 0.13);
  background:
    radial-gradient(circle at 20% 0, rgba(106, 61, 139, 0.16), transparent 38%),
    rgba(3, 2, 5, 0.62);
}

.stage-venue-preview-map :deep(.host-mini-map__svg) {
  min-height: 290px;
  background: linear-gradient(145deg, #0b080f, #030204);
}

.stage-venue-preview-map :deep(.host-mini-map__zone rect) {
  fill: rgba(102, 67, 126, 0.2);
  stroke: rgba(205, 183, 221, 0.42);
}

.stage-venue-preview-map :deep(.host-mini-map__hint) {
  display: none;
}

.stage-venue-preview-map__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-venue-preview-map__facts span {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-height: 30px;
  padding: 0 11px;
  border: 1px solid rgba(195, 175, 210, 0.1);
  border-radius: 999px;
  color: rgba(216, 206, 224, 0.64);
  background: rgba(255, 255, 255, 0.025);
  font-size: 0.72rem;
}

.stage-venue-preview-map__facts strong {
  color: #f4edf8;
}

.stage-venue-onboarding__error {
  margin: 0;
  padding: 11px 13px;
  border: 1px solid rgba(226, 116, 143, 0.2);
  border-radius: 12px;
  color: #f3ccd6;
  background: rgba(112, 37, 55, 0.16);
  line-height: 1.45;
}

.stage-venue-onboarding__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 4px;
}

.stage-venue-onboarding .workspace-button--primary {
  border-color: rgba(183, 139, 214, 0.34) !important;
  color: #fff !important;
  background: linear-gradient(135deg, #4d2768, #271331) !important;
  box-shadow: 0 12px 28px rgba(31, 13, 42, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
}

.stage-venue-onboarding .workspace-button--primary:hover:not(:disabled),
.stage-venue-onboarding .workspace-button--primary:focus-visible {
  border-color: rgba(205, 170, 228, 0.52) !important;
  background: linear-gradient(135deg, #5d3378, #32183f) !important;
}

.stage-venue-onboarding-empty {
  display: grid;
  grid-template-columns: minmax(300px, 1.1fr) minmax(260px, 0.9fr);
  align-items: center;
  gap: 32px;
  min-height: 500px;
  padding: 34px;
  overflow: hidden;
}

.stage-venue-onboarding-empty__visual {
  position: relative;
  min-height: 340px;
  overflow: hidden;
  border: 1px solid rgba(194, 166, 214, 0.12);
  border-radius: 28px;
  background:
    radial-gradient(circle at 60% 35%, rgba(102, 57, 134, 0.2), transparent 34%),
    linear-gradient(145deg, #0c0910, #030204);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.stage-venue-onboarding-empty__grid {
  position: absolute;
  inset: 0;
  opacity: 0.22;
  background-image:
    linear-gradient(rgba(180, 145, 204, 0.2) 1px, transparent 1px),
    linear-gradient(90deg, rgba(180, 145, 204, 0.2) 1px, transparent 1px);
  background-size: 34px 34px;
  mask-image: linear-gradient(to bottom, black, transparent 92%);
}

.stage-venue-onboarding-empty__zone {
  position: absolute;
  display: block;
  border: 2px solid rgba(181, 139, 211, 0.5);
  background: rgba(89, 48, 119, 0.19);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.24);
  animation: stage-venue-empty-zone 4.8s ease-in-out infinite;
}

.stage-venue-onboarding-empty__zone--one {
  top: 58px;
  left: 42px;
  width: 42%;
  height: 100px;
  border-radius: 24px 46px 20px 32px;
}

.stage-venue-onboarding-empty__zone--two {
  top: 90px;
  right: 36px;
  width: 32%;
  height: 142px;
  border-radius: 50% 24px 32px 22px;
  animation-delay: 420ms;
}

.stage-venue-onboarding-empty__zone--three {
  bottom: 44px;
  left: 82px;
  width: 52%;
  height: 92px;
  border-radius: 30px 18px 44px 20px;
  animation-delay: 840ms;
}

.stage-venue-onboarding-empty__cursor {
  position: absolute;
  right: 26%;
  bottom: 23%;
  display: inline-grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(210, 187, 226, 0.28);
  border-radius: 14px;
  color: #d5b9e7;
  background: rgba(39, 22, 51, 0.88);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.32);
  animation: stage-venue-empty-cursor 4.8s ease-in-out infinite;
}

.stage-venue-onboarding-empty__copy {
  align-content: center;
  gap: 12px;
}

.stage-venue-onboarding-empty__copy h3 {
  font-size: clamp(1.6rem, 3vw, 2.35rem);
  line-height: 1.08;
}

.stage-venue-onboarding-empty__copy .stage-venue-onboarding__actions {
  justify-content: flex-start;
  margin-top: 8px;
}

@keyframes stage-venue-preview-shimmer {
  to {
    background-position: -220% 0;
  }
}

@keyframes stage-venue-empty-zone {
  0%, 100% {
    border-color: rgba(181, 139, 211, 0.34);
    transform: translateY(0);
  }

  48% {
    border-color: rgba(211, 183, 230, 0.68);
    transform: translateY(-4px);
  }
}

@keyframes stage-venue-empty-cursor {
  0%, 100% {
    transform: translate(0, 0) rotate(-8deg);
  }

  45% {
    transform: translate(-74px, -52px) rotate(-8deg);
  }
}

.workspace-two-column {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.workspace-subpanel {
  padding: 20px;
  border-radius: 20px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
}

.workspace-subpanel h3,
.workspace-subpanel strong,
.workspace-table-title,
.workspace-limit-card__title,
.workspace-preview-field label {
  color: var(--text-main);
}

.workspace-subpanel h3,
.workspace-subpanel p,
.workspace-preview-field label,
.workspace-table-subcopy {
  margin: 0;
}

.workspace-card--soft,
.workspace-advanced-block,
.workspace-limit-card,
.workspace-guest-fields-block {
  padding: 16px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.02);
}

.workspace-chip-button--toggle {
  justify-self: flex-start;
}

.workspace-advanced-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.workspace-summary-line {
  margin: 0;
  padding: 14px 16px;
  border-radius: 14px;
  color: var(--text-soft);
  background: rgba(203, 255, 232, 0.06);
  border: 1px solid rgba(203, 255, 232, 0.12);
  grid-column: 1 / -1;
}

.workspace-summary-line--compact {
  padding: 0;
  background: transparent;
  border: 0;
}

.workspace-field--limit-inline,
.workspace-preview-field,
.workspace-assignment-controls {
  display: grid;
  gap: 8px;
}

.workspace-assignment-card {
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.workspace-assignment-card p,
.workspace-empty--compact {
  margin: 0;
}

.workspace-chip-row,
.workspace-inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-actions-cell {
  min-width: 220px;
}

.stage-table-actions {
  gap: 6px;
}

.stage-table-actions .workspace-chip-button {
  min-height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  box-shadow: none;
  background: rgba(255, 255, 255, 0.035);
}

.stage-table-actions .workspace-chip-button span {
  font-size: 0.9rem;
}

.stage-modal,
.stage-optional-stack,
.stage-code-shell,
.stage-modal-summary-card,
.stage-summary-list,
.stage-modal-checklist,
.stage-summary-inline-grid {
  display: grid;
  gap: 18px;
}

.stage-modal__section {
  position: relative;
  display: grid;
  gap: 18px;
  padding: 24px;
  border-radius: 26px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
  overflow: hidden;
  animation: stage-modal-rise 240ms ease both;
}

.stage-modal__section::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(188, 240, 236, 0.55), rgba(218, 232, 255, 0.42), transparent);
  pointer-events: none;
}

.stage-modal__section--optional {
  gap: 14px;
}

.stage-modal__section--summary {
  gap: 16px;
}

.stage-modal__section-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
}

.stage-modal__section-head--compact {
  gap: 12px;
}

.stage-modal__section-head h3,
.stage-modal-summary-card h3 {
  margin: 6px 0 0;
  color: var(--text-main);
}

.stage-modal__section-head p,
.stage-modal-summary-card p {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.6;
}

.stage-modal__grid {
  display: grid;
  gap: 16px;
}

.stage-modal__grid--base {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.stage-modal__grid--rules {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-modal__field--hero {
  grid-column: span 2;
}

.stage-base-facts,
.stage-access-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stage-access-fact {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  color: var(--text-soft);
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.035);
}

.stage-availability-picker,
.stage-scope-picker,
.stage-toggle-grid,
.stage-code-option-grid,
.stage-modal-summary-pills,
.stage-modal-footer {
  display: grid;
  gap: 12px;
}

.stage-inline-group {
  display: grid;
  gap: 10px;
}

.stage-inline-group__label {
  color: var(--text-main);
  font-weight: 700;
  letter-spacing: -0.01em;
}

.stage-inline-helper {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.55;
  max-width: 780px;
}

.stage-inline-helper strong {
  color: var(--text-main);
}

.stage-tax-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.035);
}

.stage-tax-card__head,
.stage-tax-preview {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.stage-tax-card p {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.5;
}

.stage-tax-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.stage-tax-card__included {
  grid-column: span 2;
}

.stage-tax-preview {
  padding-top: 10px;
  border-top: 1px solid rgba(196, 206, 228, 0.12);
  color: var(--text-soft);
}

.stage-tax-preview strong {
  color: var(--text-main);
}

.stage-toggle-pills {
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.stage-toggle-pill {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: var(--text-soft);
  font-weight: 700;
  text-align: center;
  transition: border-color 180ms ease, background 180ms ease, color 180ms ease;
}

.stage-toggle-pill.is-active {
  color: var(--text-main);
  border-color: rgba(196, 206, 228, 0.24);
  background: rgba(255, 255, 255, 0.08);
}

.stage-activation-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: center;
  padding-top: 2px;
}

.stage-inline-note {
  color: var(--text-soft);
  font-size: 0.9rem;
  line-height: 1.45;
}

.stage-availability-picker {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stage-availability-picker--checkout {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-scope-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-toggle-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-choice-card,
.stage-toggle-card,
.stage-code-option,
.stage-scope-pill,
.stage-code-chip,
.stage-optional-toggle {
  border: 1px solid rgba(196, 206, 228, 0.1);
  color: var(--text-main);
  background: rgba(255, 255, 255, 0.035);
}

.stage-choice-card,
.stage-toggle-card,
.stage-code-option {
  display: grid;
  gap: 6px;
  padding: 16px;
  border-radius: 16px;
  text-align: left;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.stage-choice-card.is-active,
.stage-toggle-card.is-active,
.stage-code-option.is-active {
  border-color: rgba(196, 206, 228, 0.22);
  background: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.stage-choice-card strong,
.stage-toggle-card strong,
.stage-code-option strong {
  font-size: 1rem;
}

.stage-choice-card small,
.stage-toggle-card small,
.stage-code-option small,
.stage-summary-list span,
.stage-modal-checklist li {
  color: var(--text-soft);
  line-height: 1.55;
}

.stage-choice-card__icon,
.stage-toggle-card__icon {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(213, 221, 234, 0.86);
  box-shadow: none;
}

.stage-scope-shell {
  display: grid;
  gap: 12px;
}

.stage-scope-pill {
  display: grid;
  gap: 2px;
  min-height: 54px;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 700;
  text-align: left;
  transition: border-color 180ms ease, background 180ms ease, color 180ms ease;
}

.stage-scope-pill strong {
  font-size: 0.92rem;
  line-height: 1.1;
}

.stage-scope-pill small {
  color: var(--text-soft);
  font-size: 0.72rem;
  font-weight: 700;
}

.stage-scope-pill.is-active {
  color: var(--text-main);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(196, 206, 228, 0.24);
}

.stage-venue-picker,
.stage-venue-off-card {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.025);
}

.stage-venue-picker__head,
.stage-venue-off-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.stage-venue-picker__head > div {
  display: grid;
  gap: 4px;
}

.stage-venue-picker__head strong,
.stage-venue-card__copy strong,
.stage-venue-empty strong {
  color: var(--text-main);
}

.stage-venue-picker__head span,
.stage-venue-card__copy small,
.stage-venue-card__copy em,
.stage-venue-empty span,
.stage-venue-off-card span {
  color: var(--text-soft);
  line-height: 1.45;
}

.stage-venue-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.stage-venue-visual {
  display: grid;
  gap: 12px;
}

.stage-venue-map {
  display: block;
  width: 100%;
  min-height: 280px;
  max-height: 440px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  border-radius: 22px;
  background:
    radial-gradient(circle at 50% 16%, rgba(122, 215, 204, 0.1), transparent 26%),
    rgba(3, 6, 12, 0.42);
}

.stage-venue-map__floor {
  fill: url(#stageVenueFloorDepth);
  stroke: rgba(196, 206, 228, 0.14);
  stroke-width: 2;
  filter: drop-shadow(0 22px 48px rgba(0, 0, 0, 0.28));
}

.stage-venue-map__section {
  cursor: pointer;
  outline: none;
}

.stage-venue-map__section rect,
.stage-venue-map__section ellipse,
.stage-venue-map__section polygon,
.stage-venue-map__section path {
  stroke-width: 4;
  transition: fill 160ms ease, stroke-width 160ms ease, filter 160ms ease;
}

.stage-venue-map__section:hover rect,
.stage-venue-map__section:hover ellipse,
.stage-venue-map__section:hover polygon,
.stage-venue-map__section:hover path,
.stage-venue-map__section.is-hovered rect,
.stage-venue-map__section.is-hovered ellipse,
.stage-venue-map__section.is-hovered polygon,
.stage-venue-map__section.is-hovered path,
.stage-venue-map__section:focus rect,
.stage-venue-map__section:focus ellipse,
.stage-venue-map__section:focus polygon,
.stage-venue-map__section:focus path,
.stage-venue-map__section.is-active rect,
.stage-venue-map__section.is-active ellipse,
.stage-venue-map__section.is-active polygon,
.stage-venue-map__section.is-active path {
  stroke-width: 7;
  filter: drop-shadow(0 0 12px rgba(122, 215, 204, 0.34));
}

.stage-venue-map__section text {
  fill: #f6fbff;
  font-size: 34px;
  font-weight: 900;
  pointer-events: none;
  paint-order: stroke;
  stroke: rgba(2, 5, 10, 0.82);
  stroke-width: 8px;
  stroke-linejoin: round;
}

.stage-venue-map__section .stage-venue-map__type {
  fill: rgba(238, 244, 255, 0.78);
  font-size: 22px;
  font-weight: 800;
}

.stage-venue-map__object {
  pointer-events: none;
  opacity: 0.78;
}

.stage-venue-map__object rect,
.stage-venue-map__object ellipse,
.stage-venue-map__object polygon {
  stroke-width: 3;
  filter: drop-shadow(0 10px 16px rgba(0, 0, 0, 0.24));
}

.stage-venue-map__object line,
.stage-venue-map__object path {
  fill: none;
  stroke-width: 7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.stage-venue-map__object text {
  font-size: 28px;
  font-weight: 760;
  letter-spacing: 0.1em;
  paint-order: stroke;
  stroke: rgba(2, 5, 10, 0.72);
  stroke-width: 6px;
}

.stage-venue-map__unit {
  cursor: pointer;
  outline: none;
  opacity: 0.72;
  transition: opacity 160ms ease, filter 160ms ease;
}

.stage-venue-map__unit rect {
  fill: rgba(238, 244, 255, 0.82);
  stroke: rgba(2, 5, 10, 0.6);
  stroke-width: 1.4;
}

.stage-venue-map__unit.is-table rect {
  fill: rgba(248, 245, 255, 0.92);
  stroke: rgba(185, 163, 229, 0.7);
  stroke-width: 2.3;
}

.stage-venue-map__unit.is-loose rect {
  stroke: rgba(122, 215, 204, 0.86);
  stroke-dasharray: 8 5;
}

.stage-venue-map__unit:hover,
.stage-venue-map__unit:focus,
.stage-venue-map__unit.is-active {
  opacity: 1;
  filter: drop-shadow(0 0 10px rgba(122, 215, 204, 0.38));
}

.stage-venue-map__unit.is-active rect {
  fill: rgba(122, 215, 204, 0.88);
  stroke: #f6fbff;
  stroke-width: 3;
}

.stage-venue-map__unit text {
  fill: #07101d;
  font-size: 14px;
  font-weight: 760;
  pointer-events: none;
}

.stage-venue-map__hit-layer {
  pointer-events: none;
}

.stage-venue-map__hit-zone {
  cursor: pointer;
  pointer-events: all;
}

.stage-venue-map__hit-area {
  fill: transparent;
  stroke: transparent;
  pointer-events: all;
}

.stage-venue-grid--compact {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.stage-venue-card {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 12px;
  min-height: 88px;
  padding: 14px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  border-radius: 18px;
  color: var(--text-main);
  text-align: left;
  background: rgba(255, 255, 255, 0.035);
  transition: border-color 180ms ease, background 180ms ease, transform 180ms ease;
}

.stage-venue-card.is-active {
  border-color: rgba(196, 206, 228, 0.28);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(142, 107, 196, 0.12));
  transform: translateY(-1px);
}

.stage-venue-card__swatch {
  width: 14px;
  height: 52px;
  border-radius: 999px;
  box-shadow: 0 0 18px rgba(142, 107, 196, 0.22);
}

.stage-venue-card__copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.stage-venue-card__copy strong,
.stage-venue-card__copy small,
.stage-venue-card__copy em {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-venue-card__copy em {
  font-style: normal;
  font-size: 0.78rem;
}

.stage-venue-card__check {
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: transparent;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(196, 206, 228, 0.1);
}

.stage-venue-card.is-active .stage-venue-card__check {
  color: var(--text-main);
  background: rgba(188, 240, 236, 0.14);
  border-color: rgba(188, 240, 236, 0.28);
}

.stage-venue-empty {
  display: grid;
  place-items: center;
  gap: 8px;
  min-height: 150px;
  padding: 22px;
  border: 1px dashed rgba(196, 206, 228, 0.14);
  border-radius: 18px;
  text-align: center;
}

.stage-venue-empty i,
.stage-venue-off-card i {
  color: rgba(213, 221, 234, 0.86);
}

.stage-availability-note {
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(196, 206, 228, 0.08);
  line-height: 1.5;
}

.stage-toggle-card {
  position: relative;
  grid-template-columns: auto 1fr;
  align-items: center;
}

.stage-toggle-card--info {
  cursor: default;
}

.stage-toggle-card input {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.stage-code-shell {
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.stage-code-shell--compact {
  gap: 0;
}

.stage-code-shell__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.stage-code-shell__head p {
  margin: 6px 0 0;
  color: var(--text-soft);
}

.stage-code-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-code-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
}

.stage-code-option-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-image-row,
.stage-image-controls,
.stage-image-actions {
  display: grid;
  gap: 14px;
}

.stage-image-row {
  grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
  align-items: start;
}

.stage-image-input {
  display: none;
}

.stage-image-preview {
  width: 100%;
  max-width: 220px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.02);
}

.stage-image-preview img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.stage-image-helper {
  color: var(--text-soft);
  line-height: 1.5;
}

.stage-optional-toggle {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  padding: 16px 18px;
  border-radius: 16px;
  text-align: left;
  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.stage-optional-toggle span {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.stage-optional-toggle strong,
.stage-modal-summary-card h3 {
  color: var(--text-main);
}

.stage-optional-toggle small {
  color: var(--text-soft);
  line-height: 1.5;
}

.stage-optional-card {
  display: grid;
  gap: 18px;
  padding: 20px;
  border-radius: 22px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
}

.stage-modal-summary-card {
  padding: 20px;
  border-radius: 22px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.03);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  animation: stage-modal-rise 260ms ease both;
}

.stage-summary-inline-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-modal-summary-pills {
  grid-template-columns: repeat(3, max-content);
  align-items: center;
}

.stage-summary-list {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.stage-summary-list div {
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.stage-summary-list strong {
  color: var(--text-main);
  line-height: 1.35;
}

.stage-modal-checklist {
  list-style: none;
  padding: 0;
  margin: 0;
}

.stage-modal-checklist li {
  position: relative;
  padding-left: 22px;
}

.stage-modal-checklist li::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 0;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
}

.stage-modal-checklist li.is-ready::before {
  background: #8cf3e8;
  box-shadow: 0 0 0 6px rgba(140, 243, 232, 0.12);
}

.stage-modal-footer {
  width: 100%;
  grid-template-columns: 1fr auto;
  align-items: center;
}

.stage-form-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 6px;
  padding: 16px 0 8px;
  border-top: 1px solid rgba(196, 206, 228, 0.08);
}

.stage-form-actions .workspace-button {
  width: auto;
  min-width: 148px;
}

@keyframes stage-modal-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.workspace-inline-check--single {
  width: fit-content;
}

.workspace-inline-check--compact {
  white-space: nowrap;
}

.workspace-actions--split-top {
  margin-top: 12px;
}

.workspace-preview-field span {
  color: #f59e0b;
}

.workspace-table-shell {
  overflow: auto;
}

.stages-table {
  min-width: 1180px;
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
}

.workspace-table th,
.workspace-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  text-align: left;
  color: var(--text-soft);
  vertical-align: top;
}

.workspace-table th {
  color: var(--text-main);
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

@media (max-width: 1100px) {
  .workspace-two-column,
  .workspace-advanced-grid,
  .stage-availability-picker,
  .stage-toggle-grid,
  .stage-code-option-grid,
  .stage-modal__grid--base,
  .stage-modal__grid--rules,
  .stage-tax-card__grid,
  .stage-image-row,
  .stage-summary-inline-grid,
  .stage-venue-grid {
    grid-template-columns: 1fr;
  }

  .stage-venue-control__active-row {
    flex-direction: column;
    align-items: stretch;
  }

  .stage-venue-control__actions {
    justify-content: flex-start;
  }

  .stage-modal__field--hero,
  .stage-tax-card__included {
    grid-column: span 1;
  }

  .stage-base-facts,
  .stage-access-facts {
    gap: 8px;
  }
}

@media (max-width: 900px) {
  .stage-entry-grid {
    grid-template-columns: 1fr;
  }

  .stage-venue-onboarding__shell,
  .stage-venue-onboarding-empty {
    grid-template-columns: 1fr;
  }

  .stage-venue-onboarding__options {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: none;
    overflow: visible;
  }

  .stage-venue-onboarding-empty {
    gap: 24px;
    min-height: 0;
  }

  .stage-venue-onboarding-empty__visual {
    min-height: 300px;
  }

  .stage-entry-card {
    min-height: 500px;
  }

  .stage-entry-picker__intro {
    display: grid;
  }

  .workspace-panel__header--spread,
  .workspace-subpanel__head,
  .workspace-assignment-card,
  .workspace-limit-card__header,
  .stage-modal__section-head,
  .stage-code-shell__head,
  .stage-venue-picker__head,
  .stage-modal-footer {
    display: grid;
  }

  .stage-modal-summary-pills,
  .stage-access-facts {
    grid-template-columns: 1fr;
  }

  .stage-activation-row {
    display: grid;
    justify-items: start;
  }

  .stage-summary-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stage-scope-picker {
    display: flex;
    overflow-x: auto;
    padding-bottom: 4px;
    margin-right: -6px;
    padding-right: 6px;
    scroll-snap-type: x proximity;
  }

  .stage-scope-pill {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  .stage-modal-footer {
    grid-template-columns: 1fr;
  }

  .stage-form-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .stages-table thead {
    display: none;
  }

  .stages-table,
  .stages-table tbody,
  .stages-table tr,
  .stages-table td {
    display: block;
    width: 100%;
  }

  .stages-table {
    min-width: 0;
    border: none;
    background: transparent;
  }

  .stages-table tr {
    margin-bottom: 16px;
    padding: 16px;
    border-radius: 22px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: linear-gradient(180deg, rgba(13, 20, 37, 0.98), rgba(8, 13, 24, 0.96));
    box-shadow: 0 18px 34px rgba(2, 6, 23, 0.28);
  }

  .stages-table td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  }

  .stages-table td:last-child {
    border-bottom: none;
  }

  .stages-table td::before {
    content: attr(data-label);
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(208, 219, 238, 0.68);
    font-weight: 700;
    min-width: 104px;
    flex-shrink: 0;
  }

  .stage-summary-cell,
  .stage-link-cell,
  .stage-status-cell,
  .stage-actions-cell {
    display: block;
  }

  .stage-summary-cell::before,
  .stage-link-cell::before,
  .stage-status-cell::before,
  .stage-actions-cell::before {
    display: block;
    margin-bottom: 10px;
  }

  .stage-summary-cell {
    padding-top: 0;
  }

  .stage-summary-cell .workspace-table-title {
    font-size: 1.04rem;
    line-height: 1.15;
  }

  .stage-summary-cell .workspace-table-subcopy {
    margin-top: 6px;
  }

  .stage-link-cell .workspace-inline-link,
  .stage-link-cell .workspace-table-subcopy {
    display: inline-flex;
    max-width: 100%;
  }

  .stage-status-cell .workspace-status-pill {
    display: inline-flex;
  }

  .stage-actions-cell {
    min-width: 0;
    display: block;
  }

  .stage-table-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .stage-table-actions .workspace-chip-button {
    width: 100%;
    justify-content: center;
  }

  .stage-modal-footer .workspace-button {
    width: 100%;
  }

  .stage-form-actions .workspace-button {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .stage-entry-picker {
    gap: 18px;
  }

  .stage-entry-card {
    grid-template-rows: auto auto 190px auto;
    gap: 16px;
    min-height: 0;
    padding: 18px;
    border-radius: 24px;
  }

  .stage-entry-card__status {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stage-entry-list-preview,
  .stage-entry-map-preview {
    height: 190px;
    border-radius: 18px;
  }

  .stage-entry-list-preview {
    padding: 12px;
  }

  .stage-entry-list-preview__row {
    grid-template-columns: 24px minmax(0, 1fr) 36px 20px;
    gap: 7px;
    min-height: 32px;
    margin-top: 5px;
    padding: 5px 6px;
  }

  .stage-entry-list-preview__price {
    width: 34px;
  }

  .stage-entry-picker__note {
    align-items: flex-start;
    text-align: left;
  }

  .stage-venue-onboarding__selector,
  .stage-venue-onboarding__preview,
  .stage-venue-onboarding-empty {
    padding: 17px;
    border-radius: 22px;
  }

  .stage-venue-onboarding__options {
    grid-template-columns: 1fr;
  }

  .stage-venue-onboarding__preview {
    grid-template-rows: auto minmax(250px, 1fr) auto auto;
  }

  .stage-venue-onboarding__preview-head {
    display: grid;
  }

  .stage-venue-onboarding__active-badge {
    width: fit-content;
  }

  .stage-venue-preview-loading,
  .stage-venue-preview-error,
  .stage-venue-preview-map :deep(.host-mini-map) {
    min-height: 260px;
  }

  .stage-venue-preview-map :deep(.host-mini-map__svg) {
    min-height: 230px;
  }

  .stage-venue-onboarding__actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .stage-venue-onboarding__actions .workspace-button {
    width: 100%;
  }

  .stage-venue-onboarding-empty__visual {
    min-height: 250px;
    border-radius: 20px;
  }

  .stage-venue-onboarding-empty__zone--one {
    left: 24px;
  }

  .stage-venue-onboarding-empty__zone--two {
    right: 20px;
  }

  .stage-venue-onboarding-empty__zone--three {
    left: 46px;
  }

  .stage-summary-list {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stage-entry-card,
  .stage-entry-list-preview__row,
  .stage-entry-map-preview__zones path,
  .stage-entry-map-preview__route,
  .stage-entry-map-preview__pin circle:first-child,
  .stage-entry-card__status > i,
  .stage-venue-preview-loading__map,
  .stage-venue-onboarding-empty__zone,
  .stage-venue-onboarding-empty__cursor {
    animation: none;
  }

  .stage-entry-list-preview__row {
    opacity: 1;
    transform: none;
  }

  .stage-entry-card,
  .stage-entry-card__glow,
  .stage-entry-card__action > i {
    transition: none;
  }
}
</style>
